import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
// import { returnOrDefault } from '@flexus/utilities';
import { IconTypes } from './icon-types';

/* EG: Usage
<flx-icon type="search" color="secondary" active="primary" [isActive]="true"></flx-icon>
<flx-icon type="search" active="primary" hover="secondary"></flx-icon>
*/

const returnOrDefault = (a, b) => {
  return a || b;
};

@Component({
  selector: 'flx-icon',
  templateUrl: './icon.component.html',
  styleUrls: ['./icon.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ERIconComponent implements OnInit, OnDestroy {
  private _size: string = null;
  private _color: string = null;
  private _type: IconTypes = 'circle-add';
  private _instruction: string = null;
  private _hoverColor: string = null;
  private _activeColor: string = null;
  private _enabled = false;
  private _isClickable: boolean;
  private _hasBackground: string;

  iconSize: string;
  iconColor: string;
  iconHover: string;
  iconActive: string;

  iconProperties: string;
  svgPaths = '';

  @ViewChild('svgPaths', { static: true }) innerSVG: ElementRef;
  @ViewChild('customIcon', { static: true }) icon: ElementRef;

  @Input() layout: 'right' | 'left' | 'bottom' | 'rounded' | 'center' | 'none' = 'none';
  @Input() use: 'default' | 'button' | 'action-panel' | 'center' | 'app-bar' = 'default';

  /* ============================= ICON SIZES ============================= */
  @Input() set size(s: string) {
    switch (s) {
      case 'extra-small':
        this._size = ' icon-size-extra-small';
        break;
      case 'small':
        this._size = ' icon-size-small';
        break;
      case 'medium':
        this._size = ' icon-size-medium';
        break;
      case 'large':
        this._size = ' icon-size-large';
        break;
      default:
        this._size = ' icon-size-default';
        break;
    }
  }
  get size(): string {
    return returnOrDefault(this._size, ' icon-size-default');
  }

  /* ============================= ICON COLOURS ============================= */
  @Input() set color(s: string) {
    switch (s) {
      case 'primary':
        this._color = ' icon-color-primary';
        break;
      case 'secondary':
        this._color = ' icon-color-secondary';
        break;
      case 'alert':
        this._color = ' icon-color-alert';
        break;
      case 'danger':
        this._color = ' icon-color-danger';
        break;
      case 'warning':
        this._color = ' icon-color-warning';
        break;
      case 'button':
        this._color = ' icon-color-button';
        break;
      case 'light':
        this._color = ' icon-color-light';
        break;
      case 'indicator':
        this._color = ' icon-color-indicator';
        break;
      case 'scratch':
        this._color = ' icon-color-scratch';
        break;
      default:
        this._color = ' icon-color-default';
        break;
    }
  }
  get color(): string {
    return returnOrDefault(this._color, ' icon-color-default');
  }

  /* ============================= ICON INSTRUCTION ============================= */
  @Input() set instruction(s: string) {
    this._instruction = s;
  }
  get instruction(): string {
    return returnOrDefault(this._instruction, '');
  }
  /* ============================= ICON HOVER ============================= */
  @Input() set hover(s: string) {
    switch (s) {
      case 'primary':
        this._hoverColor = ' icon-hover-primary';
        break;
      case 'secondary':
        this._hoverColor = ' icon-hover-secondary';
        break;
      case 'none':
        this._hoverColor = ' icon-hover-none';
        break;
      case 'container':
        this._hoverColor = ' icon-hover-container';
        break;
      default:
        this._hoverColor = ' icon-hover-default';
        break;
    }
  }
  get hover(): string {
    return returnOrDefault(this._hoverColor, ' icon-hover-default');
  }
  /* ============================= ICON ACTIVE ============================= */
  @Input() set active(s: string) {
    switch (s) {
      case 'primary':
        this._activeColor = ' icon-active-primary';
        break;
      case 'secondary':
        this._activeColor = ' icon-active-secondary';
        break;
      case 'action-panel':
        this._activeColor = ' icon-active-action-panel';
        break;
      case 'danger':
        this._activeColor = ' icon-active-danger';
        break;
      case 'none':
        this._activeColor = ' icon-active-none';
        break;
      default:
        this._activeColor = ' icon-active-default';
        break;
    }
  }
  get active(): string {
    return returnOrDefault(this._activeColor, ' icon-active-default');
  }

  /* ============================= ICON ENABLED ============================= */
  @Input() set isActive(s: boolean) {
    this._enabled = s;
    this.setProperties();
  }
  get isActive(): boolean {
    return returnOrDefault(this._enabled, false);
  }

  /* ============================= ICON ENABLED ============================= */
  @Input() set isClickable(s: boolean) {
    this._isClickable = s;
    this.setProperties();
  }
  get isClickable(): boolean {
    return returnOrDefault(this._isClickable, true);
  }

  /* ============================= ICON TYPES ============================= */
  @Input() set type(icon: IconTypes) {
    // Copy only the icon paths here (inside the <g></g> tags)
    this._type = icon;
    switch (icon) {
      /// General
      case 'correct':
        this.svgPaths = `
        <path d="M6.347,24.003c-0.601,0-1.182-0.183-1.68-0.529c-0.261-0.181-0.489-0.403-0.68-0.658L0.15,17.7
          c-0.12-0.16-0.171-0.358-0.143-0.556C0.036,16.946,0.14,16.77,0.3,16.65c0.131-0.098,0.286-0.15,0.45-0.15
          c0.235,0,0.459,0.112,0.6,0.3l3.839,5.118c0.094,0.127,0.207,0.236,0.335,0.325c0.245,0.17,0.53,0.26,0.826,0.26
          c0.086,0,0.173-0.008,0.259-0.023c0.381-0.068,0.712-0.281,0.933-0.599L22.636,0.32C22.775,0.12,23.005,0,23.25,0
          c0.154,0,0.303,0.047,0.429,0.135c0.165,0.115,0.274,0.287,0.309,0.484c0.035,0.197-0.009,0.396-0.124,0.561L8.772,22.739
          c-0.449,0.645-1.124,1.078-1.9,1.217C6.699,23.987,6.522,24.003,6.347,24.003z"/>`;
        break;

      case 'incorrect':
        this.svgPaths = `
        <path d="M23.25,23.999c-0.2,0-0.389-0.078-0.53-0.22L12,13.06L1.28,23.779c-0.142,0.142-0.33,0.22-0.53,0.22
          s-0.389-0.078-0.53-0.22c-0.292-0.292-0.292-0.768,0-1.061l10.72-10.72L0.22,1.279C0.078,1.138,0,0.949,0,0.749
          s0.078-0.389,0.22-0.53c0.141-0.142,0.33-0.22,0.53-0.22s0.389,0.078,0.53,0.22L12,10.938l10.72-10.72
          c0.142-0.142,0.33-0.22,0.53-0.22s0.389,0.078,0.53,0.22C23.922,0.36,24,0.549,24,0.749s-0.078,0.389-0.22,0.53l-10.72,10.72
          l10.72,10.72c0.292,0.292,0.292,0.768,0,1.061C23.639,23.921,23.45,23.999,23.25,23.999z"/>`;
        break;
      case 'download':
        this.svgPaths = `
        <path d="M17.25,24c-3.722,0-6.75-3.028-6.75-6.75s3.028-6.75,6.75-6.75S24,13.528,24,17.25S20.972,24,17.25,24z M17.25,12
\t\tC14.355,12,12,14.355,12,17.25s2.355,5.25,5.25,5.25s5.25-2.355,5.25-5.25S20.145,12,17.25,12z"/>
\t<path d="M17.25,21c-0.087,0-0.174-0.016-0.258-0.046c-0.006-0.002-0.011-0.004-0.017-0.006c-0.104-0.042-0.186-0.097-0.256-0.167
\t\tl-2.25-2.25c-0.292-0.292-0.292-0.768,0-1.061c0.142-0.142,0.33-0.22,0.53-0.22s0.389,0.078,0.53,0.22l0.97,0.97V14.25
\t\tc0-0.414,0.336-0.75,0.75-0.75S18,13.836,18,14.25v4.189l0.97-0.97c0.142-0.142,0.33-0.22,0.53-0.22s0.389,0.078,0.53,0.22
\t\tc0.292,0.292,0.292,0.768,0,1.061l-2.25,2.25c-0.07,0.07-0.152,0.125-0.245,0.163c-0.008,0.003-0.017,0.007-0.026,0.01
\t\tC17.428,20.984,17.339,21,17.25,21z"/>
\t<path d="M3.75,7.5C3.336,7.5,3,7.164,3,6.75S3.336,6,3.75,6h10.5C14.664,6,15,6.336,15,6.75S14.664,7.5,14.25,7.5H3.75z"/>
\t<path d="M3.75,12C3.336,12,3,11.664,3,11.25s0.336-0.75,0.75-0.75h6c0.414,0,0.75,0.336,0.75,0.75S10.164,12,9.75,12H3.75z"/>
\t<path d="M3.75,16.5C3.336,16.5,3,16.164,3,15.75S3.336,15,3.75,15H7.5c0.414,0,0.75,0.336,0.75,0.75S7.914,16.5,7.5,16.5H3.75z"/>
\t<path d="M2.25,21C1.009,21,0,19.991,0,18.75V2.25C0,1.009,1.009,0,2.25,0h10.629c0.601,0,1.165,0.234,1.59,0.658l2.872,2.872
\t\tC17.766,3.955,18,4.52,18,5.121V7.5c0,0.414-0.336,0.75-0.75,0.75S16.5,7.914,16.5,7.5V5.121c0-0.197-0.08-0.39-0.219-0.53
\t\tl-2.872-2.872c-0.142-0.141-0.33-0.219-0.53-0.219H2.25C1.836,1.5,1.5,1.836,1.5,2.25v16.5c0,0.414,0.336,0.75,0.75,0.75H7.5
\t\tc0.414,0,0.75,0.336,0.75,0.75S7.914,21,7.5,21H2.25z"/>
        `;
        break;

      case 'addition':
        this.svgPaths = `
            <path d="M12,17.25c-0.414,0-0.75-0.336-0.75-0.75v-3.75H7.5c-0.414,0-0.75-0.336-0.75-0.75s0.336-0.75,0.75-0.75h3.75V7.5
            c0-0.414,0.336-0.75,0.75-0.75s0.75,0.336,0.75,0.75v3.75h3.75c0.414,0,0.75,0.336,0.75,0.75s-0.336,0.75-0.75,0.75h-3.75v3.75
            C12.75,16.914,12.414,17.25,12,17.25z"/>
            <path d="M12,24C5.383,24,0,18.617,0,12C0,5.383,5.383,0,12,0c6.617,0,12,5.383,12,12C24,18.617,18.617,24,12,24z M12,1.5
            C6.21,1.5,1.5,6.21,1.5,12c0,5.79,4.71,10.5,10.5,10.5c5.79,0,10.5-4.71,10.5-10.5C22.5,6.21,17.79,1.5,12,1.5z"/>`;
        break;

      case 'subtraction':
        this.svgPaths = `
          <path d="M7.5,12.75c-0.414,0-0.75-0.336-0.75-0.75s0.336-0.75,0.75-0.75h9c0.414,0,0.75,0.336,0.75,0.75s-0.336,0.75-0.75,0.75H7.5
            z"/>
          <path d="M12,24C5.383,24,0,18.617,0,12C0,5.383,5.383,0,12,0c6.617,0,12,5.383,12,12C24,18.617,18.617,24,12,24z M12,1.5
            C6.21,1.5,1.5,6.21,1.5,12c0,5.79,4.71,10.5,10.5,10.5c5.79,0,10.5-4.71,10.5-10.5C22.5,6.21,17.79,1.5,12,1.5z"/>`;
        break;
      case 'edit':
        this.svgPaths = `
        <path d="M0.747,24.004c-0.2,0-0.388-0.078-0.53-0.219c-0.217-0.216-0.281-0.538-0.163-0.82l3.06-7.327
        c0.004-0.01,0.009-0.02,0.014-0.03c0.012-0.028,0.036-0.071,0.067-0.115c0.006-0.009,0.025-0.032,0.031-0.04
        c0.017-0.022,0.031-0.039,0.047-0.054L13.852,4.826c0.009-0.011,0.018-0.022,0.027-0.031c0.013-0.013,0.028-0.026,0.044-0.039
        l3.035-3.035C16.661,1.581,16.337,1.509,16,1.509c-0.601,0-1.164,0.232-1.587,0.655l-3.186,3.182
        c-0.142,0.141-0.33,0.219-0.53,0.219c-0.201,0-0.389-0.078-0.531-0.22c-0.292-0.292-0.292-0.768,0.001-1.061l3.186-3.182
        c0.706-0.706,1.647-1.095,2.651-1.095c0.759,0,1.501,0.231,2.121,0.655c0.629-0.419,1.378-0.647,2.138-0.647
        c0.761,0,1.5,0.223,2.135,0.646c0.829,0.589,1.373,1.454,1.542,2.443c0.168,0.989-0.058,1.983-0.638,2.799
        c-0.12,0.17-0.256,0.33-0.403,0.477L8.58,20.7c-0.013,0.013-0.029,0.026-0.045,0.038c-0.062,0.054-0.125,0.094-0.194,0.123
        l-7.301,3.085C0.945,23.985,0.848,24.004,0.747,24.004z M2.147,21.849l4.584-1.937l-2.663-2.663L2.147,21.849z M8.049,19.109
        l9.546-9.546l-3.18-3.18l-9.547,9.544L8.049,19.109z M18.656,8.503l3.182-3.182c0.088-0.089,0.169-0.185,0.241-0.286
        c0.719-1.011,0.481-2.419-0.531-3.138c-0.375-0.249-0.822-0.384-1.284-0.384c-0.642,0-1.265,0.267-1.71,0.732l-3.078,3.077
        L18.656,8.503z"/>`;
        break;

      case 'remove':
        this.svgPaths = `
          <path d="M12,23.999c-6.617,0-12-5.383-12-12c0-6.617,5.383-12,12-12c6.617,0,12,5.383,12,12C24,18.616,18.617,23.999,12,23.999z
            M12,1.499c-5.79,0-10.5,4.71-10.5,10.5c0,5.79,4.71,10.5,10.5,10.5c5.79,0,10.5-4.71,10.5-10.5C22.5,6.209,17.79,1.499,12,1.499z"/>
          <path d="M16.5,17.249c-0.2,0-0.389-0.078-0.53-0.22L12,13.06l-3.97,3.97c-0.142,0.142-0.33,0.22-0.53,0.22s-0.389-0.078-0.53-0.22
            c-0.292-0.292-0.292-0.768,0-1.061l3.97-3.97l-3.97-3.97c-0.142-0.141-0.22-0.33-0.22-0.53s0.078-0.389,0.22-0.53
            c0.141-0.142,0.33-0.22,0.53-0.22s0.389,0.078,0.53,0.22l3.97,3.97l3.97-3.97c0.142-0.142,0.33-0.22,0.53-0.22
            s0.389,0.078,0.53,0.22c0.142,0.141,0.22,0.33,0.22,0.53s-0.078,0.389-0.22,0.53l-3.97,3.97l3.97,3.97
            c0.292,0.292,0.292,0.768,0,1.061C16.889,17.171,16.7,17.249,16.5,17.249z"/>`;
        break;

      case 'chevron-down':
        this.svgPaths = `
        <path d="M12,18.999c-0.4,0-0.776-0.156-1.059-0.438L0.22,7.841C0.078,7.699,0,7.511,0,7.31c0-0.2,0.078-0.389,0.22-0.53
          c0.142-0.142,0.33-0.22,0.53-0.22s0.389,0.078,0.53,0.22L12,17.499L22.72,6.78c0.142-0.142,0.33-0.22,0.53-0.22
          s0.389,0.078,0.53,0.22C23.922,6.922,24,7.11,24,7.31c0,0.2-0.078,0.389-0.22,0.53L13.06,18.56
          C12.778,18.843,12.401,18.999,12,18.999z"/>`;
        break;

      case 'chevron-up':
        this.svgPaths = `
        <path d="M0.75,17.939c-0.2,0-0.389-0.078-0.53-0.22c-0.292-0.292-0.292-0.768,0-1.061L10.94,5.94
          C11.223,5.656,11.599,5.5,11.999,5.5c0.401,0,0.777,0.156,1.06,0.438l10.721,10.72c0.292,0.292,0.292,0.768,0,1.061
          c-0.142,0.142-0.33,0.22-0.53,0.22s-0.389-0.078-0.53-0.22L12,7L1.28,17.719C1.139,17.861,0.95,17.939,0.75,17.939z"/>`;
        break;

      case 'down-arrow':
        this.svgPaths = `
          <path d="M12,24c-0.088,0-0.175-0.016-0.26-0.047c-0.008-0.003-0.015-0.005-0.022-0.008c-0.096-0.039-0.179-0.094-0.249-0.165
          L0.97,13.28c-0.142-0.142-0.22-0.33-0.22-0.53s0.078-0.389,0.22-0.53C1.111,12.078,1.3,12,1.5,12s0.389,0.078,0.53,0.22l9.22,9.22
          V0.75C11.25,0.336,11.586,0,12,0s0.75,0.336,0.75,0.75v20.689l9.22-9.22C22.111,12.078,22.3,12,22.5,12s0.389,0.078,0.53,0.22
          c0.142,0.142,0.22,0.33,0.22,0.53s-0.078,0.389-0.22,0.53l-10.5,10.5c-0.07,0.07-0.153,0.125-0.246,0.164
          c-0.008,0.003-0.018,0.007-0.027,0.01C12.172,23.985,12.086,24,12,24z"/>
          `;

        break;

      case 'up-arrow':
        this.svgPaths = `
          <path d="M12,24c-0.414,0-0.75-0.336-0.75-0.75V2.561l-9.22,9.22C1.889,11.922,1.7,12,1.5,12s-0.389-0.078-0.53-0.22
          c-0.142-0.141-0.22-0.33-0.22-0.53s0.078-0.389,0.22-0.53L11.469,0.22c0.07-0.07,0.152-0.125,0.245-0.163
          c0.008-0.003,0.016-0.006,0.025-0.009C11.825,0.016,11.912,0,12,0c0.089,0,0.177,0.016,0.261,0.048
          c0.007,0.002,0.014,0.005,0.021,0.007c0.097,0.04,0.179,0.095,0.25,0.166L23.03,10.72c0.142,0.141,0.22,0.33,0.22,0.53
          s-0.078,0.389-0.22,0.53S22.7,12,22.5,12s-0.389-0.078-0.53-0.22l-9.22-9.22V23.25C12.75,23.664,12.414,24,12,24z"/>
        `;
        break;

      case 'right-arrow':
        this.svgPaths = `
        <path d="M5.499,24c-0.2,0-0.389-0.078-0.53-0.22c-0.292-0.292-0.292-0.768,0-1.061L15.689,12L4.969,1.28
          c-0.142-0.141-0.22-0.33-0.22-0.53s0.078-0.389,0.22-0.53C5.11,0.078,5.299,0,5.499,0s0.389,0.078,0.53,0.22l10.72,10.72
          c0.584,0.584,0.585,1.535,0.001,2.119L6.029,23.78C5.888,23.922,5.699,24,5.499,24z"/>`;
        break;

      case 'left-arrow':
        this.svgPaths = `
        <path d="M16.25,24c-0.2,0-0.389-0.078-0.53-0.22L5,13.06c-0.283-0.283-0.439-0.659-0.439-1.059c0-0.4,0.155-0.777,0.438-1.06
          L15.72,0.22C15.861,0.078,16.05,0,16.25,0s0.389,0.078,0.53,0.22C16.922,0.361,17,0.55,17,0.75s-0.078,0.389-0.22,0.53L6.06,12
          L16.78,22.72c0.142,0.142,0.22,0.33,0.22,0.53s-0.078,0.389-0.22,0.53S16.45,24,16.25,24z"/>`;
        break;

      case 'date-picker':
        this.svgPaths = `
        <path d="M2.252,24c-1.241,0-2.25-1.009-2.25-2.25V5.25C0.002,4.009,1.011,3,2.252,3h3.75V0.75C6.002,0.336,6.338,0,6.752,0
        s0.75,0.336,0.75,0.75V3h9V0.75c0-0.414,0.336-0.75,0.75-0.75s0.75,0.336,0.75,0.75V3h3.75c1.241,0,2.25,1.009,2.25,2.25v16.5
        c0,1.241-1.009,2.25-2.25,2.25H2.252z M1.502,21.75c0,0.414,0.336,0.75,0.75,0.75h19.5c0.414,0,0.75-0.336,0.75-0.75V10.5h-21
        V21.75z M22.502,9V5.25c0-0.414-0.336-0.75-0.75-0.75h-3.75V6c0,0.414-0.336,0.75-0.75,0.75s-0.75-0.336-0.75-0.75V4.5h-9V6
        c0,0.414-0.336,0.75-0.75,0.75S6.002,6.414,6.002,6V4.5h-3.75c-0.414,0-0.75,0.336-0.75,0.75V9H22.502z"/>`;
        break;

      case 'time-picker':
        this.svgPaths = `
        <path d="M12,23.25C5.797,23.25,0.75,18.203,0.75,12C0.75,5.797,5.797,0.75,12,0.75c6.203,0,11.25,5.047,11.25,11.25
          C23.25,18.203,18.203,23.25,12,23.25z M12,2.25c-5.376,0-9.75,4.374-9.75,9.75s4.374,9.75,9.75,9.75s9.75-4.374,9.75-9.75
          S17.376,2.25,12,2.25z"/>
        <path d="M16.687,17.438c-0.2,0-0.389-0.078-0.53-0.22l-4.687-4.688c-0.07-0.07-0.125-0.152-0.163-0.245
          c-0.003-0.008-0.006-0.017-0.009-0.025c-0.032-0.086-0.047-0.172-0.047-0.26V8.25c0-0.414,0.336-0.75,0.75-0.75
          s0.75,0.336,0.75,0.75v3.439l4.467,4.468c0.142,0.142,0.22,0.33,0.22,0.53s-0.078,0.389-0.22,0.53S16.887,17.438,16.687,17.438z"/>`;
        break;

      case 'sp-details':
        this.svgPaths = `
        <path d="M2.25,22.5C1.009,22.5,0,21.491,0,20.25V6.75C0,5.509,1.009,4.5,2.25,4.5H9c0-1.654,1.346-3,3-3c1.654,0,3,1.346,3,3h6.75
        C22.991,4.5,24,5.509,24,6.75v13.5c0,1.241-1.009,2.25-2.25,2.25H2.25z M21.75,21c0.414,0,0.75-0.336,0.75-0.75V6.75
        C22.5,6.336,22.164,6,21.75,6H15v1.5C15,8.327,14.327,9,13.5,9h-3C9.673,9,9,8.327,9,7.5V6H2.25C1.836,6,1.5,6.336,1.5,6.75v13.5
        C1.5,20.664,1.836,21,2.25,21h0.808c0.368-2.55,2.584-4.5,5.192-4.5s4.824,1.95,5.192,4.5H21.75z M11.924,21
        c-0.352-1.712-1.892-3-3.674-3s-3.322,1.288-3.674,3H11.924z M12,3c-0.827,0-1.5,0.673-1.5,1.5v3h3v-3C13.5,3.673,12.827,3,12,3z"
        />
      <path d="M8.25,17.25c-1.861,0-3.375-1.514-3.375-3.375c0-1.861,1.514-3.375,3.375-3.375s3.375,1.514,3.375,3.375
        C11.625,15.736,10.111,17.25,8.25,17.25z M8.25,12c-1.034,0-1.875,0.841-1.875,1.875S7.216,15.75,8.25,15.75
        s1.875-0.841,1.875-1.875S9.284,12,8.25,12z"/>
      <path d="M14.25,13.5c-0.414,0-0.75-0.336-0.75-0.75S13.836,12,14.25,12h4.5c0.414,0,0.75,0.336,0.75,0.75s-0.336,0.75-0.75,0.75
        H14.25z"/>
      <path d="M14.25,16.5c-0.414,0-0.75-0.336-0.75-0.75S13.836,15,14.25,15h6c0.414,0,0.75,0.336,0.75,0.75s-0.336,0.75-0.75,0.75
        H14.25z"/>
      `;
        break;

      case 'auto-add-details': //onsite person
        this.svgPaths = `
        <path d="M17.25,24c-3.722,0-6.75-3.028-6.75-6.75s3.028-6.75,6.75-6.75S24,13.528,24,17.25S20.972,24,17.25,24z M17.25,12
          C14.355,12,12,14.355,12,17.25s2.355,5.25,5.25,5.25s5.25-2.355,5.25-5.25S20.145,12,17.25,12z"/>
        <path d="M17.25,21c-0.414,0-0.75-0.336-0.75-0.75V18h-2.25c-0.414,0-0.75-0.336-0.75-0.75s0.336-0.75,0.75-0.75h2.25v-2.25
          c0-0.414,0.336-0.75,0.75-0.75S18,13.836,18,14.25v2.25h2.25c0.414,0,0.75,0.336,0.75,0.75S20.664,18,20.25,18H18v2.25
          C18,20.664,17.664,21,17.25,21z"/>
        <path d="M0.75,18C0.336,18,0,17.663,0,17.25c0.003-4.134,3.369-7.498,7.503-7.498c1.02,0.001,2.008,0.203,2.941,0.6
          c0.184,0.078,0.327,0.224,0.402,0.41c0.075,0.186,0.073,0.39-0.006,0.574c-0.118,0.277-0.388,0.456-0.69,0.456c0,0,0,0,0,0
          c-0.101,0-0.2-0.02-0.294-0.06c-0.747-0.318-1.537-0.48-2.349-0.48c-1.606,0-3.112,0.623-4.246,1.755
          c-1.135,1.133-1.76,2.64-1.761,4.243C1.5,17.664,1.163,18,0.75,18C0.75,18,0.75,18,0.75,18z"/>
        <path d="M7.5,9.75c-2.688,0-4.875-2.187-4.875-4.875S4.812,0,7.5,0s4.875,2.187,4.875,4.875S10.188,9.75,7.5,9.75z M7.5,1.5
          c-1.861,0-3.375,1.514-3.375,3.375S5.639,8.25,7.5,8.25s3.375-1.514,3.375-3.375S9.361,1.5,7.5,1.5z"/>`;
        break;

      case 'alert':
        this.svgPaths = `
        <circle cx="12" cy="16.873" r="1.125"/>
        <path d="M12,14.248c-0.414,0-0.75-0.336-0.75-0.75v-7.5c0-0.414,0.336-0.75,0.75-0.75s0.75,0.336,0.75,0.75v7.5
          C12.75,13.912,12.413,14.248,12,14.248z"/>
        <path d="M12,23.997c-0.684,0-1.327-0.266-1.81-0.748l-9.44-9.439c-0.998-0.998-0.998-2.623,0-3.621l9.44-9.439
          c0.482-0.483,1.124-0.75,1.807-0.751c0.685,0,1.327,0.265,1.81,0.747c0.007,0.007,9.444,9.443,9.444,9.443
          c0.997,0.999,0.997,2.623,0,3.621l-9.44,9.439C13.327,23.731,12.684,23.997,12,23.997z M11.25,22.188
          c0.2,0.199,0.466,0.309,0.75,0.309c0.284,0,0.55-0.11,0.75-0.31l9.44-9.439c0.413-0.414,0.413-1.087,0-1.501L12.749,1.81
          c-0.01-0.009-0.024-0.025-0.033-0.035c-0.172-0.169-0.437-0.276-0.713-0.276c-0.288,0-0.553,0.111-0.753,0.311l-9.441,9.44
          c-0.413,0.414-0.413,1.087,0,1.5h0L11.25,22.188z"/>`;
        break;

      /// Application-bar
      case 'workflow-waffle':
        this.svgPaths = `
        <rect id="Rectangle-path" x="0.71875" y="0.715875" width="4.3125" height="4.3125" rx="1.5"></rect>
        <rect id="Rectangle-path" x="9.34375" y="0.715875" width="4.3125" height="4.3125" rx="1.5"></rect>
        <rect id="Rectangle-path" x="17.96875" y="0.715875" width="4.3125" height="4.3125" rx="1.5"></rect>
        <rect id="Rectangle-path" x="0.71875" y="9.340875" width="4.3125" height="4.3125" rx="1.5"></rect>
        <rect id="Rectangle-path" x="9.34375" y="9.340875" width="4.3125" height="4.3125" rx="1.5"></rect>
        <rect id="Rectangle-path" x="17.96875" y="9.340875" width="4.3125" height="4.3125" rx="1.5"></rect>
        <rect id="Rectangle-path" x="0.71875" y="17.965875" width="4.3125" height="4.3125" rx="1.5"></rect>
        <rect id="Rectangle-path" x="9.34375" y="17.965875" width="4.3125" height="4.3125" rx="1.5"></rect>
        <rect id="Rectangle-path" x="17.96875" y="17.965875" width="4.3125" height="4.3125" rx="1.5"></rect>`;
        break;

      case 'default-view':
        this.svgPaths = `
        <rect y="1.997" width="24" height="9" rx="1.5" ry="1.5"/><rect y="12.997" width="24" height="9" rx="1.5" ry="1.5"/>`;
        break;

      case 'list-view':
        this.svgPaths = `
        <rect y="0.747" width="24" height="5.5" rx="1.5" ry="1.5"/><rect y="9.247" width="24" height="5.5" rx="1.5" ry="1.5"/><rect y="17.747" width="24" height="5.5" rx="1.5" ry="1.5"/>`;
        break;

      case 'refresh':
        this.svgPaths = `
        <path d="M4.917,19.499H0.75c-0.414,0-0.75-0.336-0.75-0.75s0.336-0.75,0.75-0.75h2.637c-1.262-1.813-1.924-3.994-1.875-6.22
          C1.573,8.974,2.723,6.36,4.749,4.421c1.964-1.88,4.544-2.916,7.265-2.916c1.388,0,2.743,0.271,4.027,0.804
          c0.185,0.077,0.329,0.221,0.406,0.406c0.076,0.185,0.076,0.389-0.001,0.574c-0.117,0.281-0.389,0.462-0.693,0.462
          c-0.099,0-0.196-0.019-0.288-0.058c-1.1-0.457-2.261-0.689-3.451-0.689c-2.332,0-4.544,0.888-6.228,2.5
          c-1.737,1.663-2.723,3.903-2.775,6.307c-0.04,1.844,0.483,3.64,1.489,5.158v-2.721c0-0.414,0.336-0.75,0.75-0.75S6,13.835,6,14.249
          v4.5c0,0.414-0.336,0.75-0.75,0.75H5.012c-0.016,0.002-0.031,0.003-0.045,0.003C4.955,19.502,4.934,19.5,4.917,19.499z"/>
        <path d="M11.995,22.501c-1.392,0-2.75-0.272-4.038-0.808c-0.185-0.077-0.329-0.221-0.405-0.407
          c-0.076-0.185-0.076-0.389,0.001-0.574c0.117-0.281,0.389-0.462,0.693-0.462c0.099,0,0.195,0.019,0.287,0.057
          c1.105,0.46,2.269,0.694,3.462,0.694c0,0,0,0,0,0c2.336,0,4.55-0.891,6.234-2.508c1.735-1.665,2.718-3.906,2.767-6.31
          c0.038-1.845-0.488-3.64-1.497-5.158v2.724c0,0.414-0.336,0.75-0.75,0.75S18,10.162,18,9.749v-4.5c0-0.414,0.336-0.75,0.75-0.75
          h4.5c0.414,0,0.75,0.336,0.75,0.75s-0.336,0.75-0.75,0.75h-2.635c1.263,1.811,1.927,3.99,1.882,6.215
          c-0.058,2.804-1.204,5.419-3.228,7.362C17.303,21.462,14.72,22.501,11.995,22.501L11.995,22.501z"/>`;
        break;

      //// WORKFLOW
      case 'circle-add':
        this.svgPaths = `
          <path d="M12,17.25c-0.414,0-0.75-0.336-0.75-0.75v-3.75H7.5c-0.414,0-0.75-0.336-0.75-0.75s0.336-0.75,0.75-0.75h3.75V7.5
            c0-0.414,0.336-0.75,0.75-0.75s0.75,0.336,0.75,0.75v3.75h3.75c0.414,0,0.75,0.336,0.75,0.75s-0.336,0.75-0.75,0.75h-3.75v3.75
            C12.75,16.914,12.414,17.25,12,17.25z"/>
          <path d="M12,24C5.383,24,0,18.617,0,12C0,5.383,5.383,0,12,0c6.617,0,12,5.383,12,12C24,18.617,18.617,24,12,24z M12,1.5
            C6.21,1.5,1.5,6.21,1.5,12c0,5.79,4.71,10.5,10.5,10.5c5.79,0,10.5-4.71,10.5-10.5C22.5,6.21,17.79,1.5,12,1.5z"/>`;
        break;

      //// WORKFLOW

      case 'search':
        this.svgPaths = `
          <path d="M23.245,23.996c-0.2,0-0.389-0.078-0.53-0.22L16.2,17.26c-0.761,0.651-1.618,1.182-2.553,1.579
            c-1.229,0.522-2.52,0.787-3.837,0.787c-1.257,0-2.492-0.241-3.673-0.718c-2.431-0.981-4.334-2.849-5.359-5.262
            c-1.025-2.412-1.05-5.08-0.069-7.51S3.558,1.802,5.97,0.777C7.199,0.254,8.489-0.01,9.807-0.01c1.257,0,2.492,0.242,3.673,0.718
            c2.431,0.981,4.334,2.849,5.359,5.262c1.025,2.413,1.05,5.08,0.069,7.51c-0.402,0.996-0.956,1.909-1.649,2.718l6.517,6.518
            c0.292,0.292,0.292,0.768,0,1.061C23.634,23.918,23.445,23.996,23.245,23.996z M9.807,1.49c-1.115,0-2.209,0.224-3.25,0.667
            C4.513,3.026,2.93,4.638,2.099,6.697c-0.831,2.059-0.81,4.318,0.058,6.362c0.869,2.044,2.481,3.627,4.54,4.458
            c1.001,0.404,2.048,0.608,3.112,0.608c1.115,0,2.209-0.224,3.25-0.667c0.974-0.414,1.847-0.998,2.594-1.736
            c0.01-0.014,0.021-0.026,0.032-0.037c0.016-0.016,0.031-0.029,0.045-0.039c0.763-0.771,1.369-1.693,1.786-2.728
            c0.831-2.059,0.81-4.318-0.059-6.362c-0.868-2.044-2.481-3.627-4.54-4.458C11.918,1.695,10.871,1.49,9.807,1.49z"/>`;
        break;

      case 'bucket':
        this.svgPaths = `
          <path d="M9.103,21.759c-1.189-0.001-2.306-0.464-3.146-1.306c0,0-3.492-3.492-3.492-3.493c-0.839-0.84-1.301-1.958-1.3-3.147
            c0.001-1.19,0.463-2.307,1.303-3.146l1.628-1.629V3.756c0-2.068,1.682-3.75,3.75-3.75c1.872,0,3.453,1.398,3.71,3.229
            c0.262,0.099,0.5,0.252,0.698,0.45l8.691,8.693c0.218,0.219,0.357,0.508,0.391,0.813c0.041,0.363-0.062,0.72-0.29,1.005
            c-0.228,0.286-0.553,0.465-0.916,0.506l-2.39,0.266l-5.486,5.486c-0.844,0.843-1.961,1.305-3.148,1.305H9.103z M7.018,19.394
            c0.557,0.558,1.298,0.865,2.086,0.866c0.789,0,1.529-0.306,2.086-0.863c0.004-0.003,5.675-5.675,5.675-5.675
            c0.119-0.12,0.278-0.196,0.447-0.215l2.376-0.265l-8.092-8.094v6.858c0,0.414-0.336,0.75-0.75,0.75s-0.75-0.336-0.75-0.75V5.159
            l-6.568,6.57c-0.557,0.556-0.864,1.297-0.864,2.086c0,0.789,0.306,1.53,0.861,2.086l0,0C3.526,15.901,7.018,19.394,7.018,19.394z
            M7.845,1.506c-1.241,0-2.25,1.009-2.25,2.25v3.782l3.852-3.854c0.172-0.171,0.373-0.309,0.594-0.407
            C9.82,2.264,8.905,1.506,7.845,1.506z"/>
          <path d="M20.246,23.989c-0.695,0-1.347-0.27-1.837-0.761c-0.792-0.792-0.988-1.997-0.487-2.999l1.654-3.308
            c0.128-0.256,0.385-0.415,0.671-0.415s0.543,0.159,0.671,0.415l1.654,3.308c0.501,1.002,0.306,2.207-0.487,3
            C21.593,23.719,20.94,23.989,20.246,23.989z M19.263,20.9c-0.212,0.424-0.129,0.933,0.206,1.268
            c0.207,0.208,0.483,0.323,0.778,0.323c0.293,0,0.568-0.114,0.774-0.32c0.336-0.337,0.42-0.847,0.208-1.27l-0.984-1.967L19.263,20.9
            z"/>`;
        break;

      case 'reminder':
        this.svgPaths = `
        <path d="M11.99,24.001c-0.47,0-0.939-0.12-1.356-0.348c-0.665-0.363-1.149-0.964-1.362-1.691c-0.117-0.397,0.111-0.814,0.508-0.931
          c0.069-0.02,0.14-0.03,0.211-0.03c0.33,0,0.626,0.221,0.72,0.538c0.101,0.343,0.329,0.626,0.642,0.798
          c0.197,0.107,0.417,0.164,0.638,0.164c0.127,0,0.255-0.019,0.379-0.055c0.438-0.129,0.777-0.468,0.906-0.906
          c0.093-0.317,0.389-0.539,0.72-0.539c0.071,0,0.141,0.01,0.21,0.03c0.397,0.117,0.626,0.534,0.509,0.931
          c-0.273,0.931-0.992,1.65-1.923,1.923C12.53,23.962,12.26,24.001,11.99,24.001z"/>
        <path d="M2.991,19.501c-0.285,0-0.549-0.165-0.674-0.421c-0.127-0.26-0.094-0.563,0.083-0.791c0.006-0.009,1.341-1.939,1.341-7.788
          c0-4.293,3.266-7.835,7.5-8.216V0.751c0-0.414,0.336-0.75,0.75-0.75s0.75,0.336,0.75,0.75v1.534c4.234,0.381,7.5,3.923,7.5,8.216
          c0,6.303,1.246,7.686,1.258,7.699c0.218,0.199,0.297,0.532,0.187,0.818c-0.114,0.293-0.387,0.483-0.695,0.483H2.991z
          M19.701,18.001c-0.482-1.232-0.961-3.47-0.961-7.5c0-3.722-3.028-6.75-6.75-6.75s-6.75,3.028-6.75,6.75
          c0,3.957-0.58,6.277-1.046,7.5H19.701z"/>`;
        break;

      case 'scratch-pad':
        this.svgPaths = `
        <path d="M7.431,16.258c-0.201,0-0.389-0.078-0.531-0.22c-0.185-0.185-0.261-0.458-0.197-0.712l1.06-4.243
          c0.033-0.132,0.101-0.252,0.197-0.349L18.037,0.659c0.425-0.424,0.99-0.658,1.591-0.658s1.166,0.234,1.591,0.658l1.061,1.06
          c0.679,0.679,0.844,1.714,0.44,2.562l0.621,0.62c0.877,0.877,0.878,2.304,0.001,3.182l-3.312,3.311
          c-0.142,0.142-0.33,0.219-0.53,0.219s-0.389-0.078-0.53-0.22c-0.292-0.292-0.292-0.768,0-1.061l3.311-3.31
          c0.292-0.293,0.292-0.769,0-1.061l-0.53-0.53l-9.547,9.546c-0.096,0.096-0.217,0.165-0.349,0.198l-4.241,1.061
          C7.554,16.251,7.492,16.258,7.431,16.258z M8.461,14.477l2.828-0.708l5.687-5.687l-2.122-2.121l-5.687,5.687L8.461,14.477z
          M18.037,7.023l3.183-3.182c0.292-0.292,0.293-0.768,0-1.061l-1.061-1.06c-0.141-0.141-0.33-0.219-0.53-0.219
          c-0.2,0-0.389,0.078-0.53,0.219l-3.183,3.181L18.037,7.023z"/>
        <path d="M2.249,24c-1.241,0-2.25-1.009-2.25-2.25V2.25C-0.001,1.01,1.008,0,2.249,0h11.25c0.414,0,0.75,0.336,0.75,0.75
          s-0.336,0.75-0.75,0.75H2.249c-0.414,0-0.75,0.336-0.75,0.75v19.5c0,0.414,0.336,0.75,0.75,0.75H13.5v-3.75
          c0-1.241,1.009-2.25,2.25-2.25h3.75v-3c0-0.414,0.336-0.75,0.75-0.75S21,13.087,21,13.5v3.128c0,0.315-0.065,0.623-0.195,0.914
          c-0.002,0.004-0.01,0.022-0.012,0.026c-0.113,0.245-0.265,0.464-0.453,0.652l-5.121,5.121c-0.187,0.188-0.406,0.34-0.652,0.453
          c-0.004,0.002-0.029,0.014-0.034,0.015C14.252,23.935,13.944,24,13.628,24H2.249z M15.75,18C15.336,18,15,18.337,15,18.75v2.688
          L18.438,18H15.75z"/>`;
        break;

      case 'scratch-pad-trash':
        this.svgPaths = `<path d="M6.631,23.25c-1.161,0-2.146-0.906-2.242-2.064L3.06,5.25H1.5c-0.414,0-0.75-0.336-0.75-0.75S1.086,3.75,1.5,3.75h6V3
        c0-1.241,1.009-2.25,2.25-2.25h4.5c1.241,0,2.25,1.009,2.25,2.25v0.75h6c0.414,0,0.75,0.336,0.75,0.75s-0.336,0.75-0.75,0.75h-1.56
        l-1.328,15.937c-0.096,1.157-1.081,2.063-2.242,2.063H6.631z M5.883,21.062c0.032,0.386,0.36,0.688,0.748,0.688H17.37
        c0.387,0,0.716-0.302,0.747-0.688L19.435,5.25H4.565L5.883,21.062z M15,3.75V3c0-0.414-0.336-0.75-0.75-0.75h-4.5
        C9.336,2.25,9,2.586,9,3v0.75H15z"/>
      <path d="M9.75,18C9.336,18,9,17.664,9,17.25v-7.5C9,9.336,9.336,9,9.75,9s0.75,0.336,0.75,0.75v7.5C10.5,17.664,10.164,18,9.75,18z
        "/>
      <path d="M14.25,18c-0.414,0-0.75-0.336-0.75-0.75v-7.5C13.5,9.336,13.836,9,14.25,9S15,9.336,15,9.75v7.5
        C15,17.664,14.664,18,14.25,18z"/>`;
        break;

      /////////// FLOW
      case 'claim-card':
        this.svgPaths = `
        <path d="M17.25,24c-3.722,0-6.75-3.028-6.75-6.75s3.028-6.75,6.75-6.75S24,13.528,24,17.25S20.972,24,17.25,24z M17.25,12
          C14.355,12,12,14.355,12,17.25s2.355,5.25,5.25,5.25s5.25-2.355,5.25-5.25S20.145,12,17.25,12z"/>
        <path d="M17.25,21c-0.414,0-0.75-0.336-0.75-0.75v-3c0-0.414,0.336-0.75,0.75-0.75S18,16.836,18,17.25v3
          C18,20.664,17.664,21,17.25,21z"/>
        <circle cx="17.25" cy="14.686" r="1.125"/>
        <path d="M3.75,7.5C3.336,7.5,3,7.164,3,6.75S3.336,6,3.75,6h10.5C14.664,6,15,6.336,15,6.75S14.664,7.5,14.25,7.5H3.75z"/>
        <path d="M3.75,12C3.336,12,3,11.664,3,11.25s0.336-0.75,0.75-0.75h6c0.414,0,0.75,0.336,0.75,0.75S10.164,12,9.75,12H3.75z"/>
        <path d="M3.75,16.5C3.336,16.5,3,16.164,3,15.75S3.336,15,3.75,15H7.5c0.414,0,0.75,0.336,0.75,0.75S7.914,16.5,7.5,16.5H3.75z"/>
        <path d="M2.25,21C1.009,21,0,19.991,0,18.75V2.25C0,1.009,1.009,0,2.25,0h10.629c0.601,0,1.165,0.234,1.59,0.658l2.872,2.872
          C17.766,3.955,18,4.52,18,5.121V7.5c0,0.414-0.336,0.75-0.75,0.75S16.5,7.914,16.5,7.5V5.121c0-0.197-0.08-0.39-0.219-0.53
          l-2.872-2.872c-0.142-0.141-0.33-0.219-0.53-0.219H2.25C1.836,1.5,1.5,1.836,1.5,2.25v16.5c0,0.414,0.336,0.75,0.75,0.75H7.5
          c0.414,0,0.75,0.336,0.75,0.75S7.914,21,7.5,21H2.25z"/>`;
        break;

      case 'job-card':
        this.svgPaths = `
        <path d="M4.5,24c-1.241,0-2.25-1.009-2.25-2.25V6c0-1.241,1.009-2.25,2.25-2.25h3.063C7.924,1.621,9.81,0,12,0
            c2.19,0,4.076,1.621,4.437,3.75H19.5c1.241,0,2.25,1.009,2.25,2.25v15.75c0,1.241-1.009,2.25-2.25,2.25H4.5z M4.5,5.25
            C4.086,5.25,3.75,5.586,3.75,6v15.75c0,0.414,0.336,0.75,0.75,0.75h15c0.414,0,0.75-0.336,0.75-0.75V6
            c0-0.414-0.336-0.75-0.75-0.75h-3.75C15.336,5.25,15,4.914,15,4.5c0-1.654-1.346-3-3-3c-1.654,0-3,1.346-3,3
            c0,0.414-0.336,0.75-0.75,0.75H4.5z"/>
          <circle cx="12" cy="4.125" r="1.125"/>
          <path d="M6.75,11.25C6.336,11.25,6,10.914,6,10.5s0.336-0.75,0.75-0.75H12c0.414,0,0.75,0.336,0.75,0.75s-0.336,0.75-0.75,0.75
            H6.75z"/>
          <path d="M15,11.25c-0.414,0-0.75-0.336-0.75-0.75S14.586,9.75,15,9.75h2.25c0.414,0,0.75,0.336,0.75,0.75s-0.336,0.75-0.75,0.75H15
            z"/>
          <path d="M12,15c-0.414,0-0.75-0.336-0.75-0.75S11.586,13.5,12,13.5h5.25c0.414,0,0.75,0.336,0.75,0.75S17.664,15,17.25,15H12z"/>
          <path d="M6.75,15C6.336,15,6,14.664,6,14.25s0.336-0.75,0.75-0.75H9c0.414,0,0.75,0.336,0.75,0.75S6.75,15,6.75,15z"/>
          <path d="M6.75,18.75C6.336,18.75,6,18.414,6,18s0.336-0.75,0.75-0.75H12c0.414,0,0.75,0.336,0.75,0.75s-0.336,0.75-0.75,0.75H6.75z
            "/>
          <path d="M15,18.75c-0.414,0-0.75-0.336-0.75-0.75s0.336-0.75,0.75-0.75h2.25c0.414,0,0.75,0.336,0.75,0.75s-0.336,0.75-0.75,0.75
            H15z"/>`;
        break;

      case 'documents':
        this.svgPaths = `
        <path d="M4.396,23.997c-0.827-0.001-1.547-0.59-1.713-1.4L0.089,9.589c-0.021-0.11-0.033-0.22-0.033-0.329
          c-0.003-0.467,0.176-0.907,0.505-1.24c0.328-0.333,0.767-0.517,1.234-0.52h6.451c0.828,0.002,1.548,0.59,1.713,1.4l0.278,1.394
          c0.023,0.111,0.128,0.196,0.244,0.196h11.827c0.115,0,0.227,0.012,0.335,0.033c0.947,0.186,1.566,1.108,1.379,2.055
          c0,0.001,0,0.002,0,0.002c-0.486,2.435-1.586,7.957-1.995,10.013c-0.167,0.813-0.887,1.403-1.713,1.404H4.396z M1.801,8.999
          C1.737,8.999,1.675,9.026,1.628,9.073C1.581,9.121,1.556,9.183,1.556,9.25c0,0.016,0.002,0.031,0.005,0.047L4.154,22.3
          c0.023,0.111,0.127,0.196,0.244,0.196h15.916c0.115,0,0.22-0.086,0.243-0.2c0.408-2.053,1.508-7.575,1.994-10.01
          c0.026-0.134-0.062-0.265-0.198-0.292c-0.015-0.003-0.032-0.005-0.048-0.005H10.479c-0.828-0.002-1.548-0.59-1.713-1.4L8.488,9.195
          C8.466,9.084,8.361,8.999,8.245,8.999H1.801z"/>
        <path d="M19.559,8.998c-0.412,0-0.749-0.335-0.75-0.747l-0.021-6.5c0-0.14-0.112-0.252-0.25-0.252H5.576
          c-0.138,0-0.25,0.112-0.25,0.25l-0.02,3.504c-0.002,0.411-0.339,0.746-0.75,0.746c-0.418-0.002-0.752-0.341-0.75-0.754l0.02-3.5
          c0-0.962,0.785-1.746,1.75-1.746h12.962c0.965,0,1.75,0.785,1.75,1.75l0.021,6.498c0.001,0.2-0.077,0.389-0.218,0.531
          c-0.141,0.142-0.329,0.221-0.53,0.221c0,0-0.001,0-0.001,0H19.559z"/>
        <path d="M7.567,4.49c-0.414,0-0.75-0.337-0.749-0.751c0-0.413,0.337-0.749,0.75-0.749l8.239,0.008c0.414,0,0.75,0.337,0.749,0.751
          c0,0.413-0.337,0.749-0.75,0.749C15.806,4.498,7.567,4.49,7.567,4.49z"/>
        <path d="M12.056,7.498c-0.414,0-0.75-0.336-0.75-0.75s0.336-0.75,0.75-0.75h3.75c0.414,0,0.75,0.336,0.75,0.75
          s-0.336,0.75-0.75,0.75H12.056z"/>`;
        break;

      case 'time-stamp':
        this.svgPaths = `
          <path d="M3.78,24c-1.237-0.006-2.242-1.016-2.242-2.25v-0.769c-0.929-0.156-1.537-1.255-1.537-2.834c0-1.094,0.3-2.189,0.822-3.002
            C1.514,14.069,2.536,13.5,3.78,13.5h3.729c1.247,0,1.779-0.595,1.779-1.989c0-1.011-0.512-1.837-1.106-2.794
            C7.53,7.666,6.856,6.579,6.856,5.152c0-2.828,2.3-5.128,5.128-5.128s5.128,2.3,5.128,5.128c0,1.428-0.674,2.514-1.326,3.565
            c-0.594,0.957-1.106,1.783-1.106,2.794c0,1.394,0.532,1.989,1.779,1.989h2.983c1.503,0,2.731,0.549,3.551,1.588
            C23.633,15.899,24,17.016,24,18.153c0,1.552-0.635,2.671-1.57,2.828v0.769c0,1.234-1.004,2.244-2.238,2.25
            c-0.001,0-0.001,0-0.002,0H3.78z M3.038,21.75c0,0.411,0.335,0.748,0.746,0.75h16.404c0.408-0.002,0.742-0.339,0.742-0.75V21H3.038
            V21.75z M22.151,19.5c0.107-0.097,0.349-0.541,0.349-1.347s-0.25-1.584-0.685-2.135C21.283,15.342,20.484,15,19.442,15h-2.983
            c-2.084,0-3.279-1.272-3.279-3.489c0-1.439,0.677-2.53,1.332-3.585c0.566-0.913,1.1-1.775,1.1-2.774c0-2-1.627-3.628-3.628-3.628
            S8.356,3.151,8.356,5.152c0,1,0.535,1.862,1.101,2.774c0.655,1.055,1.331,2.146,1.331,3.585c0,2.217-1.195,3.489-3.279,3.489H3.78
            c-0.728,0-1.282,0.312-1.695,0.955c-0.371,0.578-0.584,1.377-0.584,2.192c0,0.812,0.218,1.256,0.315,1.353H22.151z"/>`;
        break;

      case 'notes':
        this.svgPaths = `
        <path d="M2.25,24C1.009,24,0,22.991,0,21.75V2.25C0,1.009,1.009,0,2.25,0h19.5C22.991,0,24,1.009,24,2.25v11.379
          c0,0.317-0.066,0.625-0.196,0.916c-0.002,0.005-0.009,0.018-0.011,0.023c-0.109,0.239-0.262,0.461-0.451,0.65l-8.123,8.122
          c-0.19,0.19-0.411,0.343-0.659,0.456c-0.006,0.003-0.026,0.012-0.032,0.014C14.254,23.934,13.946,24,13.629,24H2.25z M2.25,1.5
          C1.836,1.5,1.5,1.836,1.5,2.25v19.5c0,0.414,0.336,0.75,0.75,0.75H13.5v-6.75c0-1.241,1.009-2.25,2.25-2.25h6.75V2.25
          c0-0.414-0.336-0.75-0.75-0.75H2.25z M15.75,15C15.336,15,15,15.336,15,15.75v5.689L21.439,15H15.75z"/>
        <path d="M6.75,7.5C6.336,7.5,6,7.164,6,6.75S6.336,6,6.75,6h12c0.414,0,0.75,0.336,0.75,0.75S19.164,7.5,18.75,7.5H6.75z"/>
        <path d="M6.75,12C6.336,12,6,11.664,6,11.25s0.336-0.75,0.75-0.75H12c0.414,0,0.75,0.336,0.75,0.75S12.414,12,12,12H6.75z"/>`;
        break;

      case 'call-log':
        this.svgPaths = `
          <path d="M14.785,23.271c-0.997,0-1.974-0.287-2.826-0.829l-0.038-0.026c-4.39-2.966-8.128-6.704-11.1-11.102
          c-0.006-0.009-0.023-0.036-0.029-0.047c-1.323-2.077-1.026-4.76,0.728-6.514l0.63-0.629C2.574,3.7,3.139,3.467,3.74,3.467
          c0.602,0,1.167,0.234,1.591,0.658l2.652,2.651c0.425,0.425,0.659,0.99,0.658,1.592c0,0.602-0.234,1.167-0.659,1.591
          c-0.292,0.292-0.292,0.768,0,1.06l4.242,4.243c0.141,0.141,0.33,0.219,0.53,0.219c0.2,0,0.389-0.078,0.53-0.219
          c0.425-0.425,0.99-0.658,1.592-0.658s1.167,0.234,1.591,0.658l2.645,2.644c0.433,0.433,0.667,0.998,0.667,1.599
          c0,0.601-0.235,1.166-0.66,1.591l-0.628,0.629C17.496,22.722,16.179,23.271,14.785,23.271L14.785,23.271z M3.741,4.966
          c-0.201,0-0.389,0.078-0.53,0.219L2.58,5.815c-1.253,1.253-1.463,3.172-0.511,4.667c2.86,4.232,6.463,7.834,10.703,10.699
          c0.002,0.001,0.021,0.015,0.023,0.016c0.586,0.373,1.278,0.575,1.984,0.575c0.996,0,1.938-0.394,2.653-1.108l0.629-0.629
          c0.142-0.142,0.22-0.33,0.22-0.531c0-0.198-0.076-0.385-0.215-0.526l-2.658-2.656c-0.142-0.142-0.33-0.219-0.531-0.219
          s-0.389,0.078-0.53,0.219c-0.425,0.425-0.99,0.658-1.592,0.658c-0.601,0-1.166-0.234-1.591-0.658l-4.242-4.243
          c-0.877-0.877-0.877-2.304,0-3.182c0.142-0.142,0.22-0.33,0.22-0.53c0-0.2-0.078-0.389-0.219-0.531L4.271,5.186
          C4.13,5.044,3.941,4.966,3.741,4.966z"/>
        <path d="M17.232,2.262c-0.414,0-0.75-0.336-0.75-0.75s0.336-0.75,0.75-0.75h6c0.414,0,0.75,0.336,0.75,0.75s-0.336,0.75-0.75,0.75
          H17.232z"/>
        <path d="M17.232,6.762c-0.414,0-0.75-0.336-0.75-0.75s0.336-0.75,0.75-0.75h6c0.414,0,0.75,0.336,0.75,0.75s-0.336,0.75-0.75,0.75
          H17.232z"/>
        <path d="M17.232,11.262c-0.414,0-0.75-0.336-0.75-0.75s0.336-0.75,0.75-0.75h6c0.414,0,0.75,0.336,0.75,0.75s-0.336,0.75-0.75,0.75
          H17.232z"/>
        <path d="M12.732,2.262c-0.414,0-0.75-0.336-0.75-0.75s0.336-0.75,0.75-0.75h1.5c0.414,0,0.75,0.336,0.75,0.75
          s-0.336,0.75-0.75,0.75H12.732z"/>
        <path d="M12.732,6.762c-0.414,0-0.75-0.336-0.75-0.75s0.336-0.75,0.75-0.75h1.5c0.414,0,0.75,0.336,0.75,0.75
          s-0.336,0.75-0.75,0.75H12.732z"/>
        <path d="M12.732,11.262c-0.414,0-0.75-0.336-0.75-0.75s0.336-0.75,0.75-0.75h1.5c0.414,0,0.75,0.336,0.75,0.75
          s-0.336,0.75-0.75,0.75H12.732z"/>`;
        break;

      case 'filter-funnel':
        this.svgPaths = `
          <path d="M10.475,23.649c-0.827,0-1.501-0.673-1.501-1.5V12.87C4.041,11.591,0.37,7.208,0.004,2.108C0.002,2.071,0,2.038,0,2.005
          c0-0.4,0.156-0.777,0.439-1.061c0.283-0.283,0.66-0.44,1.061-0.44h20.952c0.033,0,0.067,0.001,0.1,0.003
          c0.398,0.026,0.763,0.207,1.027,0.508c0.264,0.301,0.396,0.687,0.37,1.088c0,0.003,0,0.007-0.001,0.01
          c-0.369,5.099-4.041,9.481-8.974,10.757v6.779c0.001,0.447-0.197,0.867-0.541,1.153l-2.999,2.499
          C11.165,23.525,10.825,23.649,10.475,23.649C10.475,23.649,10.475,23.649,10.475,23.649z M1.501,2.004
          c0.347,4.679,3.787,8.598,8.373,9.535c0.348,0.071,0.6,0.38,0.6,0.735v9.875l3-2.5v-7.375c0-0.355,0.252-0.664,0.6-0.735
          c4.596-0.937,8.041-4.859,8.378-9.537L1.501,2.004z"/>
          `;
        break;

      case 'query-history':
        this.svgPaths = `
          <path d="M9.75,11.251c-0.161,0-0.321-0.053-0.45-0.15l-2.8-2.1H2.25C1.009,9.001,0,7.991,0,6.751v-4.5
            c0-1.241,1.009-2.25,2.25-2.25h9c1.241,0,2.25,1.009,2.25,2.25v4.5c0,1.241-1.009,2.25-2.25,2.25H10.5v1.5
            c0,0.286-0.159,0.543-0.414,0.671C9.981,11.223,9.865,11.251,9.75,11.251z M2.25,1.501c-0.414,0-0.75,0.336-0.75,0.75v4.5
            c0,0.414,0.336,0.75,0.75,0.75h4.5c0.161,0,0.321,0.053,0.45,0.15L9,9.001v-0.75c0-0.414,0.336-0.75,0.75-0.75h1.5
            c0.414,0,0.75-0.336,0.75-0.75v-4.5c0-0.414-0.336-0.75-0.75-0.75H2.25z"/>
          <path d="M14.25,24.001c-0.116,0-0.233-0.028-0.336-0.079c-0.255-0.128-0.414-0.385-0.414-0.671v-1.5h-0.75
            c-1.241,0-2.25-1.009-2.25-2.25v-4.5c0-1.241,1.009-2.25,2.25-2.25h9c1.241,0,2.25,1.009,2.25,2.25v4.5
            c0,1.241-1.009,2.25-2.25,2.25H17.5l-2.8,2.1C14.571,23.948,14.411,24.001,14.25,24.001z M12.75,14.251
            c-0.414,0-0.75,0.336-0.75,0.75v4.5c0,0.414,0.336,0.75,0.75,0.75h1.5c0.414,0,0.75,0.336,0.75,0.75v0.75l1.8-1.35
            c0.129-0.097,0.289-0.15,0.45-0.15h4.5c0.414,0,0.75-0.336,0.75-0.75v-4.5c0-0.414-0.336-0.75-0.75-0.75H12.75z"/>
          <path d="M4.974,20.251c-1.861,0-3.375-1.514-3.375-3.375s1.514-3.375,3.375-3.375s3.375,1.514,3.375,3.375
            S6.835,20.251,4.974,20.251z M4.974,15.001c-1.034,0-1.875,0.841-1.875,1.875s0.841,1.875,1.875,1.875s1.875-0.841,1.875-1.875
            S6.008,15.001,4.974,15.001z"/>
          <path d="M9.2,24.001c-0.318,0-0.603-0.202-0.708-0.503C7.972,22.004,6.557,21,4.973,21c-0.416,0-0.827,0.07-1.223,0.208
            c-1.079,0.376-1.914,1.211-2.29,2.29c-0.105,0.301-0.39,0.503-0.708,0.503c-0.085,0-0.168-0.014-0.248-0.042
            c-0.188-0.065-0.34-0.201-0.427-0.382c-0.087-0.18-0.099-0.384-0.033-0.573c0.528-1.514,1.699-2.685,3.213-3.212
            C3.811,19.598,4.388,19.5,4.972,19.5c2.222,0,4.206,1.408,4.937,3.504c0.066,0.189,0.054,0.393-0.033,0.573
            c-0.087,0.181-0.239,0.316-0.429,0.382C9.369,23.987,9.285,24.001,9.2,24.001z"/>
          <path d="M19.025,7.501c-1.861,0-3.375-1.514-3.375-3.375s1.514-3.375,3.375-3.375S22.4,2.265,22.4,4.126S20.886,7.501,19.025,7.501
            z M19.025,2.251c-1.034,0-1.875,0.841-1.875,1.875s0.841,1.875,1.875,1.875S20.9,5.16,20.9,4.126S20.059,2.251,19.025,2.251z"/>
          <path d="M14.801,11.251c-0.083,0-0.166-0.014-0.246-0.042c-0.19-0.066-0.343-0.202-0.43-0.383
            c-0.087-0.181-0.099-0.384-0.032-0.573c0.528-1.513,1.699-2.684,3.212-3.212c0.555-0.193,1.132-0.292,1.716-0.292
            c2.222,0,4.206,1.408,4.937,3.504c0.066,0.189,0.055,0.393-0.032,0.573c-0.087,0.18-0.239,0.316-0.428,0.382
            c-0.08,0.028-0.163,0.042-0.247,0.042c-0.318,0-0.603-0.202-0.708-0.503C22.02,9.254,20.606,8.25,19.021,8.25
            c-0.416,0-0.827,0.07-1.222,0.208c-1.079,0.376-1.914,1.211-2.29,2.29C15.404,11.049,15.119,11.251,14.801,11.251z"/>
          <path d="M18.75,19.501c-0.2,0-0.389-0.078-0.53-0.22c-0.292-0.292-0.292-0.768,0-1.061l0.22-0.22H14.25
            c-0.414,0-0.75-0.336-0.75-0.75s0.336-0.75,0.75-0.75h4.189l-0.22-0.22c-0.292-0.292-0.292-0.768,0-1.061
            c0.142-0.142,0.33-0.22,0.53-0.22s0.389,0.078,0.53,0.22l1.5,1.5c0.07,0.07,0.125,0.152,0.163,0.245
            c0.003,0.008,0.007,0.017,0.01,0.026C20.983,17.073,21,17.161,21,17.251c0,0.087-0.016,0.174-0.046,0.258
            c-0.002,0.006-0.004,0.011-0.006,0.017c-0.042,0.104-0.097,0.186-0.167,0.256l-1.5,1.5C19.138,19.423,18.95,19.501,18.75,19.501z"
            />
          <path d="M5.25,6.751c-0.2,0-0.389-0.078-0.53-0.22l-1.5-1.5c-0.07-0.07-0.125-0.152-0.163-0.245C3.053,4.778,3.05,4.77,3.047,4.761
            C3.015,4.675,3,4.588,3,4.501C3,4.412,3.016,4.324,3.047,4.24C3.05,4.232,3.052,4.225,3.055,4.219
            C3.095,4.122,3.15,4.04,3.22,3.969L4.719,2.47c0.142-0.142,0.33-0.22,0.53-0.22s0.389,0.078,0.53,0.22C5.922,2.612,6,2.8,6,3.001
            S5.922,3.39,5.78,3.531l-0.22,0.22H9.75c0.414,0,0.75,0.336,0.75,0.75s-0.336,0.75-0.75,0.75H5.56l0.22,0.22
            C5.922,5.612,6,5.8,6,6.001S5.922,6.39,5.78,6.531C5.638,6.673,5.45,6.751,5.25,6.751z"/>`;
        break;

      case 'policy-history':
        this.svgPaths = `<path d="M17.25,24c-3.722,0-6.75-3.028-6.75-6.75s3.028-6.75,6.75-6.75S24,13.528,24,17.25S20.972,24,17.25,24z M17.25,12
              C14.355,12,12,14.355,12,17.25s2.355,5.25,5.25,5.25s5.25-2.355,5.25-5.25S20.145,12,17.25,12z"/>
            <path d="M17.25,18c-0.414,0-0.75-0.336-0.75-0.75v-2.652c0-0.414,0.336-0.75,0.75-0.75S18,14.184,18,14.598V16.5h1.902
              c0.414,0,0.75,0.336,0.75,0.75S20.316,18,19.902,18H17.25z"/>
            <path d="M3.75,7.5C3.336,7.5,3,7.164,3,6.75S3.336,6,3.75,6h10.5C14.664,6,15,6.336,15,6.75S14.664,7.5,14.25,7.5H3.75z"/>
            <path d="M3.75,12C3.336,12,3,11.664,3,11.25s0.336-0.75,0.75-0.75h6c0.414,0,0.75,0.336,0.75,0.75S10.164,12,9.75,12H3.75z"/>
            <path d="M3.75,16.5C3.336,16.5,3,16.164,3,15.75S3.336,15,3.75,15H7.5c0.414,0,0.75,0.336,0.75,0.75S7.914,16.5,7.5,16.5H3.75z"/>
            <path d="M2.25,21C1.009,21,0,19.991,0,18.75V2.25C0,1.009,1.009,0,2.25,0h10.629c0.601,0,1.165,0.234,1.59,0.658l2.872,2.872
              C17.766,3.955,18,4.52,18,5.121V7.5c0,0.414-0.336,0.75-0.75,0.75S16.5,7.914,16.5,7.5V5.121c0-0.197-0.08-0.39-0.219-0.53
              l-2.872-2.872c-0.142-0.141-0.33-0.219-0.53-0.219H2.25C1.836,1.5,1.5,1.836,1.5,2.25v16.5c0,0.414,0.336,0.75,0.75,0.75H7.5
              c0.414,0,0.75,0.336,0.75,0.75S7.914,21,7.5,21H2.25z"/>`;
        break;

      case 'internal-assessor-notes':
        this.svgPaths = `
        <path d="M24,13.6V2.3C24,1,23,0,21.8,0H2.3C1,0,0,1,0,2.3v19.5C0,23,1,24,2.3,24h11.4c0.3,0,0.6-0.1,0.9-0.2
        c0,0,0,0,0.1,0c0.2-0.1,0.4-0.3,0.6-0.4l8.1-8.1c0.2-0.2,0.3-0.4,0.4-0.6c0,0,0,0,0,0C23.9,14.2,24,13.9,24,13.6z M1.5,21.8V2.3
        c0-0.4,0.3-0.8,0.8-0.8h19.5c0.4,0,0.8,0.3,0.8,0.8v11.3h-6.8c-1.2,0-2.3,1-2.3,2.3v6.8H2.3C1.8,22.5,1.5,22.2,1.5,21.8z M15,21.4
        v-5.7c0-0.4,0.3-0.8,0.8-0.8h5.7L15,21.4z M6.3,11V3.8h1.4V11H6.3z M18.3,11h-1.6l-0.6-1.6h-2.9L12.6,11h-1.5l2.8-7.2h1.5L18.3,11z
        M15.6,8.2l-1-2.7l-1,2.7H15.6z"/>
      `;
        break;

      ///// CLAIM CAPTURE

      case 'what-matters':
        this.svgPaths = `
          <path d="M15.501,24c-0.414,0-0.75-0.336-0.75-0.75v-3.739c0-0.221,0.096-0.429,0.265-0.572c0.136-0.115,0.308-0.178,0.485-0.178
            c0.041,0,0.082,0.003,0.123,0.01l1.008,0.168c0.124,0.021,0.249,0.031,0.374,0.031c0.469,0,0.919-0.145,1.303-0.419
            c0.489-0.349,0.813-0.868,0.912-1.461c0.02-0.122,0.031-0.247,0.031-0.371v-2.458c0-0.345,0.234-0.644,0.568-0.728l0.935-0.234
            c0.194-0.049,0.358-0.17,0.461-0.342s0.133-0.373,0.084-0.568c-0.021-0.084-0.056-0.162-0.103-0.234l-1.819-2.729
            c-0.083-0.124-0.126-0.268-0.126-0.416v-0.75c0-1.701-0.756-3.327-2.13-4.578c-1.544-1.406-3.65-2.18-5.93-2.18
            c-0.311,0-0.627,0.015-0.939,0.044c-4.529,0.411-7.878,4.429-7.467,8.956c0.242,2.666,1.771,5.057,4.09,6.395
            c0.231,0.133,0.375,0.382,0.375,0.649v5.7c0,0.414-0.336,0.75-0.75,0.75s-0.75-0.336-0.75-0.75v-5.278
            c-2.526-1.615-4.187-4.339-4.458-7.33C1.057,8.05,1.846,5.523,3.514,3.521c1.668-2.001,4.012-3.233,6.6-3.468
            c0.357-0.033,0.719-0.051,1.076-0.051c2.655,0,5.12,0.913,6.94,2.572c1.689,1.539,2.62,3.558,2.62,5.687v0.523l1.693,2.54
            c0.144,0.215,0.248,0.452,0.31,0.703c0.146,0.582,0.056,1.187-0.253,1.703c-0.309,0.516-0.801,0.88-1.384,1.025l-0.367,0.092v1.873
            c0,0.206-0.017,0.414-0.051,0.617c-0.165,0.988-0.704,1.853-1.519,2.435c-0.641,0.458-1.392,0.699-2.173,0.699l0,0
            c-0.208,0-0.417-0.017-0.624-0.052l-0.134-0.022v2.854C16.251,23.664,15.914,24,15.501,24z"/>
          <path d="M11.573,13.5c-0.414,0-0.75-0.336-0.75-0.75v-0.256c-0.001-0.952,0.602-1.805,1.5-2.122c0.897-0.317,1.5-1.17,1.5-2.121
            c0-1.24-1.009-2.25-2.25-2.25c-1.24,0-2.25,1.009-2.25,2.25C9.323,8.664,8.986,9,8.573,9s-0.75-0.337-0.75-0.75
            c0-2.068,1.683-3.749,3.75-3.749c2.068,0,3.75,1.683,3.75,3.751c0,1.586-1.005,3.006-2.5,3.535c-0.299,0.106-0.5,0.39-0.5,0.707
            v0.257C12.323,13.164,11.986,13.5,11.573,13.5z"/>
          <circle cx="11.573" cy="16.125" r="1.125"/>`;
        break;

      ///// APP MENU

      case 'app-menu-hamburger':
        this.svgPaths = `
        <path d="M23,20.3H1c-0.6,0-1-0.4-1-1V19c0-0.6,0.4-1,1-1h22c0.6,0,1,0.4,1,1v0.3C24,19.8,23.6,20.3,23,20.3z
          M24,10.3V10c0-0.6-0.4-1-1-1H1c-0.6,0-1,0.4-1,1v0.3c0,0.6,0.4,1,1,1h22C23.6,11.3,24,10.8,24,10.3z M24,1.3V1c0-0.6-0.4-1-1-1H1
          C0.4,0,0,0.4,0,1v0.3c0,0.6,0.4,1,1,1h22C23.6,2.3,24,1.8,24,1.3z"/>
        `;
        break;

      case 'app-menu-dashboard':
        this.svgPaths = `
          <path d="M8.246,20.25c-0.2,0-0.389-0.078-0.53-0.22c-0.292-0.292-0.292-0.768,0-1.061l3.97-3.97c0.283-0.283,0.659-0.439,1.06-0.44
          c0.401,0,0.778,0.156,1.061,0.439l1.94,1.94l3.969-3.969c0.142-0.142,0.33-0.22,0.53-0.22s0.389,0.078,0.53,0.22
          c0.292,0.292,0.292,0.768,0,1.061l-3.969,3.969c-0.284,0.283-0.66,0.439-1.061,0.439c-0.401,0-0.778-0.156-1.061-0.439
          l-1.939-1.939l-3.97,3.97C8.635,20.172,8.446,20.25,8.246,20.25z"/>
          <path d="M2.246,24c-1.241,0-2.25-1.009-2.25-2.25V2.25c0-1.241,1.009-2.25,2.25-2.25h19.5c1.241,0,2.25,1.009,2.25,2.25v19.5
            c0,1.241-1.009,2.25-2.25,2.25H2.246z M2.246,1.5c-0.414,0-0.75,0.336-0.75,0.75v19.5c0,0.414,0.336,0.75,0.75,0.75h19.5
            c0.414,0,0.75-0.336,0.75-0.75V2.25c0-0.414-0.336-0.75-0.75-0.75H2.246z"/>
          <path d="M8.246,13.5c-2.895,0-5.25-2.355-5.25-5.25S5.351,3,8.246,3s5.25,2.355,5.25,5.25c0,1.383-0.535,2.688-1.507,3.677
            c-0.01,0.013-0.02,0.025-0.031,0.035c-0.01,0.011-0.023,0.021-0.036,0.031C10.94,12.962,9.633,13.5,8.246,13.5z M7.496,4.578
            c-1.712,0.352-3,1.892-3,3.672c0,2.068,1.682,3.75,3.75,3.75c0.735,0,1.455-0.219,2.066-0.623L7.716,8.78
            c-0.142-0.142-0.22-0.33-0.22-0.53V4.578z M11.373,10.316c0.404-0.611,0.623-1.331,0.623-2.066c0-1.781-1.288-3.32-3-3.672v3.362
            L11.373,10.316z"/>
          <path d="M16.496,6c-0.414,0-0.75-0.336-0.75-0.75s0.336-0.75,0.75-0.75h3c0.414,0,0.75,0.336,0.75,0.75S19.91,6,19.496,6H16.496z"
            />
          <path d="M16.496,9c-0.414,0-0.75-0.336-0.75-0.75s0.336-0.75,0.75-0.75h3c0.414,0,0.75,0.336,0.75,0.75S19.91,9,19.496,9H16.496z"
            />
          `;
        break;

      case 'app-menu-new-claim':
        this.svgPaths = `
          <path d="M11.246,24c-0.2,0-0.389-0.078-0.53-0.22c-0.178-0.177-0.255-0.431-0.205-0.677l0.75-3.75
            c0.029-0.146,0.1-0.279,0.205-0.384l7.631-7.63c0.54-0.541,1.26-0.839,2.026-0.839c0.764,0,1.483,0.297,2.022,0.835
            c0.545,0.546,0.846,1.27,0.847,2.037c0.001,0.767-0.297,1.487-0.839,2.027l-7.627,7.629c-0.105,0.105-0.238,0.177-0.384,0.205
            l-3.749,0.75C11.344,23.995,11.295,24,11.246,24z M12.202,22.294l2.424-0.485l7.467-7.469c0.533-0.532,0.535-1.399,0.003-1.932
            c-0.012-0.011-0.026-0.025-0.03-0.03c-0.241-0.24-0.579-0.379-0.941-0.379c-0.366,0-0.71,0.142-0.968,0.401l-7.471,7.47
            L12.202,22.294z"/>
          <path d="M2.242,21c-1.241,0-2.25-1.009-2.25-2.25V2.25c0-1.241,1.009-2.25,2.25-2.25h15c1.241,0,2.25,1.009,2.25,2.25V9
            c0,0.414-0.336,0.75-0.75,0.75s-0.75-0.336-0.75-0.75V2.25c0-0.414-0.336-0.75-0.75-0.75h-15c-0.414,0-0.75,0.336-0.75,0.75v16.5
            c0,0.414,0.336,0.75,0.75,0.75h6c0.414,0,0.75,0.336,0.75,0.75S8.655,21,8.242,21H2.242z"/>
          <path d="M8.242,6c-0.414,0-0.75-0.336-0.75-0.75S7.828,4.5,8.242,4.5h6c0.414,0,0.75,0.336,0.75,0.75S14.655,6,14.242,6H8.242z"/>
          <path d="M5.242,10.5c-0.414,0-0.75-0.336-0.75-0.75S4.828,9,5.242,9h9c0.414,0,0.75,0.336,0.75,0.75s-0.336,0.75-0.75,0.75H5.242z"
            />
          <path d="M5.242,15c-0.414,0-0.75-0.336-0.75-0.75s0.336-0.75,0.75-0.75h7.5c0.414,0,0.75,0.336,0.75,0.75S13.155,15,12.742,15
            H5.242z"/>
          `;
        break;

      case 'app-menu-workflow':
        this.svgPaths = `
          <path d="M3,23.247c-1.241,0-2.25-1.009-2.25-2.25v-18c0-1.241,1.009-2.25,2.25-2.25h18c1.241,0,2.25,1.009,2.25,2.25v18
          c0,1.241-1.009,2.25-2.25,2.25H3z M21,21.747c0.414,0,0.75-0.336,0.75-0.75v-5.25H8.25v6H21z M2.25,20.997
          c0,0.414,0.336,0.75,0.75,0.75h3.75v-13.5h-4.5V20.997z M21.75,14.247v-6H8.25v6H21.75z M21.75,6.747v-3.75
          c0-0.414-0.336-0.75-0.75-0.75H3c-0.414,0-0.75,0.336-0.75,0.75v3.75H21.75z"/>
          `;
        break;

      case 'information':
        this.svgPaths = `
          <g>
            <path d="M13.5,17.25c-1.241,0-2.25-1.009-2.25-2.25v-3.75H10.5c-0.414,0-0.75-0.336-0.75-0.75s0.336-0.75,0.75-0.75h0.75
            c0.827,0,1.5,0.673,1.5,1.5V15c0,0.414,0.336,0.75,0.75,0.75h0.75c0.414,0,0.75,0.336,0.75,0.75s-0.336,0.75-0.75,0.75H13.5z"/>
            <circle cx="11.625" cy="7.125" r="1.125"/>
            <path d="M12,24C5.383,24,0,18.617,0,12C0,5.383,5.383,0,12,0c6.617,0,12,5.383,12,12C24,18.617,18.617,24,12,24z M12,1.5
            C6.21,1.5,1.5,6.21,1.5,12c0,5.79,4.71,10.5,10.5,10.5c5.79,0,10.5-4.71,10.5-10.5C22.5,6.21,17.79,1.5,12,1.5z"/>
          </g>
        `;
        break;

      case 'app-menu-maps':
        this.svgPaths = `
          <path d="M8.25,20.768c-0.287,0-0.568-0.054-0.835-0.161l-6-2.4C0.555,17.863,0,17.043,0,16.117V1.744
          c-0.001-0.191,0.035-0.381,0.108-0.562C0.257,0.81,0.542,0.519,0.91,0.361C1.099,0.281,1.297,0.24,1.5,0.24
          c0.191,0,0.379,0.036,0.559,0.109l5.913,2.366c0.086,0.034,0.176,0.052,0.267,0.053c0.004,0,0.018,0,0.022,0
          c0.091-0.001,0.182-0.019,0.268-0.054l6.385-2.555c0.268-0.107,0.549-0.162,0.836-0.162c0.287,0,0.568,0.054,0.835,0.162l6,2.4
          C23.445,2.902,24,3.722,24,4.648v4.35c0,0.414-0.336,0.75-0.75,0.75s-0.75-0.336-0.75-0.75v-4.35c0-0.309-0.185-0.582-0.471-0.697
          L16.5,1.74v6.508c0,0.414-0.336,0.75-0.75,0.75S15,8.662,15,8.248V1.74L9.086,4.106C9.057,4.118,9.029,4.128,9,4.139v14.887
          l2.409-0.964c0.089-0.036,0.183-0.054,0.279-0.054c0.308,0,0.582,0.185,0.696,0.471c0.074,0.187,0.072,0.39-0.007,0.574
          s-0.225,0.327-0.411,0.401l-2.881,1.153C8.817,20.714,8.536,20.768,8.25,20.768z M1.5,16.117c0,0.309,0.185,0.582,0.472,0.697
          L7.5,19.025V4.139c-0.029-0.01-0.057-0.021-0.086-0.032L1.501,1.741L1.5,16.117z"/>
        <path d="M18.75,16.948c-0.62,0-1.125-0.505-1.125-1.125c0-0.573,0.427-1.048,0.995-1.115c0.042-0.007,0.086-0.011,0.13-0.011
          s0.088,0.004,0.131,0.011c0.513,0.06,0.923,0.47,0.983,0.983c0.008,0.042,0.011,0.086,0.011,0.131s-0.004,0.088-0.011,0.131
          c-0.06,0.509-0.466,0.918-0.974,0.982C18.843,16.944,18.796,16.948,18.75,16.948z"/>
        <path d="M18.749,23.998c-0.337,0-0.656-0.11-0.922-0.317c-0.097-0.076-0.185-0.164-0.26-0.26c-1.855-2.372-4.067-5.616-4.067-7.598
          c0-2.895,2.355-5.25,5.25-5.25S24,12.927,24,15.822c0,1.979-2.212,5.225-4.068,7.6C19.646,23.788,19.215,23.998,18.749,23.998z
          M18.75,12.072c-2.068,0-3.75,1.682-3.75,3.75c0,1.161,1.437,3.719,3.75,6.676c2.313-2.959,3.75-5.517,3.75-6.676
          C22.5,13.754,20.818,12.072,18.75,12.072z"/>`;
        break;

      case 'app-menu-reports':
        this.svgPaths = `
          <path d="M0.75,24C0.336,24,0,23.664,0,23.25s0.336-0.75,0.75-0.75H1.5V18c0-0.827,0.673-1.5,1.5-1.5h3c0.827,0,1.5,0.673,1.5,1.5
          v4.5H9v-12C9,9.673,9.673,9,10.5,9h3c0.827,0,1.5,0.673,1.5,1.5v12h1.5V15c0-0.827,0.673-1.5,1.5-1.5h3c0.827,0,1.5,0.673,1.5,1.5
          v7.5h0.75c0.414,0,0.75,0.336,0.75,0.75S23.664,24,23.25,24H0.75z M21,22.5V15h-3l0,7.5H21z M13.5,22.5v-12h-3l0,12H13.5z M6,22.5
          V18H3l0,4.5H6z"/>
          <path d="M2.25,12.75C1.009,12.75,0,11.741,0,10.5s1.009-2.25,2.25-2.25c0.259,0,0.518,0.046,0.762,0.134l4.065-4.968
          C6.865,3.066,6.75,2.661,6.75,2.25C6.75,1.009,7.759,0,9,0c1.2,0,2.184,0.944,2.247,2.129l8.681,2.553
          C20.348,4.1,21.022,3.75,21.75,3.75C22.991,3.75,24,4.759,24,6s-1.009,2.25-2.25,2.25c-1.2,0-2.184-0.945-2.247-2.13l-8.681-2.553
          C10.402,4.149,9.729,4.5,9,4.5c-0.259,0-0.518-0.046-0.762-0.134L4.173,9.334C4.385,9.684,4.5,10.089,4.5,10.5
          C4.5,11.741,3.491,12.75,2.25,12.75z M2.25,9.75c-0.414,0-0.75,0.336-0.75,0.75s0.336,0.75,0.75,0.75S3,10.914,3,10.5
          S2.664,9.75,2.25,9.75z M21.75,5.25C21.336,5.25,21,5.586,21,6s0.336,0.75,0.75,0.75S22.5,6.414,22.5,6S22.164,5.25,21.75,5.25z
          M9,1.5c-0.414,0-0.75,0.336-0.75,0.75S8.586,3,9,3s0.75-0.336,0.75-0.75S9.414,1.5,9,1.5z"/>
          `;
        break;

      case 'app-menu-settings':
        this.svgPaths = `
          <path d="M2.251,24c-1.241,0-2.25-1.009-2.25-2.25V2.25C0.001,1.009,1.01,0,2.251,0h19.5c1.241,0,2.25,1.009,2.25,2.25v19.5
          c0,1.241-1.009,2.25-2.25,2.25H2.251z M2.251,1.5c-0.414,0-0.75,0.336-0.75,0.75v19.5c0,0.414,0.336,0.75,0.75,0.75h19.5
          c0.414,0,0.75-0.336,0.75-0.75V2.25c0-0.414-0.336-0.75-0.75-0.75H2.251z"/>
        <path d="M10.501,18.75c-1.37,0-2.564-0.947-2.902-2.25H5.251c-0.414,0-0.75-0.336-0.75-0.75S4.837,15,5.251,15h2.349
          c0.338-1.303,1.531-2.25,2.9-2.25c1.371,0,2.565,0.947,2.903,2.25h5.348c0.414,0,0.75,0.336,0.75,0.75s-0.336,0.75-0.75,0.75
          h-5.348C13.065,17.803,11.871,18.75,10.501,18.75z M10.5,14.25c-0.827,0-1.499,0.673-1.499,1.5c0,0.827,0.673,1.5,1.5,1.5
          s1.5-0.673,1.5-1.5C12.001,14.923,11.328,14.25,10.5,14.25l0-0.375L10.5,14.25z"/>
        <path d="M16.5,11.25c-1.369-0.001-2.562-0.948-2.9-2.25H5.251c-0.414,0-0.75-0.336-0.75-0.75S4.837,7.5,5.251,7.5h8.348
          c0.338-1.303,1.532-2.25,2.902-2.25c1.654,0,3,1.346,3,3C19.5,9.904,18.154,11.25,16.5,11.25L16.5,11.25z M16.501,6.75
          c-0.827,0-1.5,0.673-1.5,1.5c0,0.827,0.673,1.5,1.5,1.5c0.827,0,1.5-0.673,1.5-1.5C18.001,7.423,17.328,6.75,16.501,6.75z"/>
        `;
        break;

      case 'app-menu-logout':
        this.svgPaths = `
        <path d="M14.244,22.507c-0.414,0-0.75-0.336-0.75-0.75s0.336-0.75,0.75-0.75h4.5c0.414,0,0.75-0.336,0.75-0.75v-1.5
        c0-0.414,0.336-0.75,0.75-0.75s0.75,0.336,0.75,0.75v1.5c0,1.241-1.009,2.25-2.25,2.25H14.244z"/>
        <path d="M20.244,7.507c-0.414,0-0.75-0.336-0.75-0.75v-1.5c0-0.414-0.336-0.75-0.75-0.75h-4.5c-0.414,0-0.75-0.336-0.75-0.75
          s0.336-0.75,0.75-0.75h4.5c1.241,0,2.25,1.009,2.25,2.25v1.5C20.994,7.17,20.657,7.507,20.244,7.507z"/>
        <path d="M10.493,24.006c-0.071,0-0.143-0.005-0.214-0.016l-8.997-1.285c-0.734-0.105-1.287-0.743-1.288-1.484V3.728
          C-0.007,3.024,0.492,2.407,1.18,2.26l8.977-2.216c0.117-0.025,0.225-0.037,0.333-0.037c0.705,0,1.324,0.498,1.471,1.184
          c0.022,0.103,0.033,0.209,0.033,0.315v21c0,0.401-0.156,0.778-0.44,1.061C11.271,23.85,10.894,24.006,10.493,24.006L10.493,24.006z
          M1.517,3.722L1.493,3.73l0.001,17.49c0,0,9.001,1.285,9.002,1.285l-0.002-21L1.517,3.722z"/>
        <path d="M20.245,16.506c-0.2,0-0.389-0.078-0.53-0.22s-0.22-0.33-0.22-0.53s0.078-0.389,0.22-0.53l1.72-1.72h-6.441
          c-0.414,0-0.75-0.336-0.75-0.75s0.336-0.75,0.75-0.75h6.439l-1.72-1.72c-0.142-0.141-0.22-0.33-0.22-0.53s0.078-0.389,0.22-0.53
          s0.33-0.22,0.53-0.22s0.389,0.078,0.53,0.22c0,0,2.995,2.994,3.001,3c0.015,0.015,0.031,0.035,0.045,0.054
          c0.041,0.049,0.074,0.102,0.101,0.158c0.015,0.032,0.026,0.063,0.036,0.096c0.016,0.053,0.026,0.103,0.031,0.154
          c0.004,0.042,0.005,0.072,0.004,0.103c-0.004,0.063-0.015,0.122-0.034,0.186c-0.01,0.034-0.02,0.063-0.033,0.093
          c-0.026,0.058-0.062,0.115-0.106,0.167c-0.013,0.019-0.028,0.036-0.042,0.051l-3,3C20.634,16.427,20.445,16.506,20.245,16.506z"/>
        <path d="M7.869,13.132c-0.62,0-1.125-0.505-1.125-1.125c0-0.573,0.425-1.05,0.992-1.117c0.044-0.008,0.088-0.012,0.133-0.012
          c0.622,0.002,1.126,0.507,1.125,1.127C8.994,12.625,8.489,13.132,7.869,13.132z"/>
            `;
        break;

      /// CLAIM CARD

      case 'star':
        this.svgPaths = `
          <path d="M18.894,24.004c-0.242,0-0.485-0.058-0.702-0.167L12,20.771l-6.187,3.064C5.596,23.944,5.361,24,5.117,24
          c-0.595,0-1.131-0.33-1.398-0.861c-0.172-0.342-0.212-0.734-0.114-1.103l1.85-6.721L0.462,10.37
          c-0.295-0.293-0.459-0.682-0.461-1.098C0,8.857,0.16,8.466,0.452,8.17c0.26-0.263,0.607-0.424,0.976-0.455l6.018-0.596l3.156-6.257
          c0.193-0.377,0.518-0.654,0.917-0.782c0.157-0.051,0.32-0.076,0.483-0.076c0.246,0,0.493,0.06,0.714,0.173
          c0.295,0.151,0.531,0.387,0.681,0.682c0.001,0.003,3.162,6.265,3.162,6.265l6.03,0.598c0.845,0.07,1.48,0.823,1.409,1.677
          c-0.031,0.369-0.192,0.715-0.455,0.976l-4.989,4.944l1.849,6.715c0.222,0.838-0.275,1.698-1.108,1.919
          C19.165,23.986,19.03,24.004,18.894,24.004z M12,19.184c0.115,0,0.231,0.027,0.334,0.079l6.528,3.232
          c0.012,0.006,0.022,0.009,0.032,0.009c0.05-0.011,0.07-0.046,0.061-0.079l-1.963-7.131c-0.072-0.261,0.003-0.541,0.195-0.732
          l5.3-5.253c0.009-0.009,0.015-0.021,0.016-0.034c0.002-0.03-0.021-0.057-0.05-0.06l-6.452-0.639
          c-0.257-0.026-0.479-0.178-0.596-0.408L12.06,1.539c-0.006-0.011-0.016-0.021-0.029-0.028c-0.009-0.005-0.02-0.007-0.031-0.007
          c-0.04,0.009-0.053,0.02-0.062,0.037L8.6,8.163c-0.116,0.23-0.339,0.383-0.596,0.409l-6.44,0.638
          C1.532,9.212,1.521,9.223,1.518,9.226C1.507,9.237,1.502,9.25,1.502,9.265c0,0.015,0.005,0.028,0.016,0.038l5.304,5.253
          c0.192,0.19,0.267,0.47,0.195,0.732l-1.965,7.138c-0.002,0.006-0.002,0.022,0.006,0.039c0.013,0.025,0.039,0.034,0.057,0.034
          c0.01,0,0.019-0.002,0.027-0.006l6.524-3.231C11.769,19.211,11.885,19.184,12,19.184z"/>`;
        break;

      case 'storms':
        this.svgPaths = `
        <path d="M11.159,22.184c-0.2,0-0.389-0.078-0.53-0.22c-0.292-0.292-0.292-0.768,0-1.061l2.47-2.47h-2.689
        c-0.305,0-0.577-0.182-0.693-0.463c-0.117-0.281-0.053-0.602,0.163-0.817l1.72-1.719H8.909c-0.675,0-1.347-0.153-1.949-0.442
        c-1.083-0.519-1.898-1.428-2.297-2.561c-0.399-1.133-0.332-2.353,0.187-3.435c0.519-1.083,1.429-1.899,2.561-2.297
        c0.484-0.171,0.988-0.257,1.495-0.257c0.37,0,0.738,0.046,1.096,0.137c0.902-2.754,3.486-4.645,6.414-4.645
        c1.811,0.002,3.505,0.706,4.779,1.983c1.273,1.276,1.973,2.972,1.971,4.775c-0.002,1.803-0.706,3.497-1.983,4.77
        c-1.274,1.271-2.967,1.971-4.767,1.971H13.72l-1.5,1.5h2.689c0.304,0,0.576,0.182,0.693,0.463s0.053,0.602-0.163,0.817l-3.75,3.75
        C11.548,22.106,11.359,22.184,11.159,22.184z M8.906,7.942C8.568,7.942,8.233,8,7.909,8.114C7.154,8.379,6.548,8.923,6.202,9.644
        c-0.346,0.721-0.39,1.534-0.124,2.289c0.266,0.755,0.809,1.361,1.53,1.707c0.401,0.192,0.849,0.294,1.294,0.294h7.506
        c1.407,0,2.724-0.544,3.715-1.533c0.993-0.991,1.54-2.308,1.542-3.711c0.002-1.402-0.543-2.722-1.533-3.714
        c-0.99-0.993-2.308-1.54-3.711-1.542c-2.524,0-4.693,1.795-5.163,4.268c0,0.001-0.001,0.005-0.001,0.007
        c-0.064,0.321-0.097,0.65-0.098,0.977c0,0.213,0.013,0.431,0.04,0.646c0.005,0.04,0.007,0.071,0.007,0.102
        c0,0.377-0.282,0.697-0.656,0.744c-0.032,0.004-0.063,0.006-0.094,0.006c-0.377,0-0.696-0.282-0.744-0.656
        C9.677,9.249,9.659,8.966,9.659,8.687C9.66,8.473,9.671,8.262,9.692,8.052C9.431,7.979,9.168,7.942,8.906,7.942z"/>
      <path d="M4.409,22.559c-0.62,0-1.125-0.505-1.125-1.125s0.505-1.125,1.125-1.125c0.021,0,0.042,0.001,0.063,0.002l3.157-3.157
        c0.142-0.142,0.33-0.22,0.53-0.22c0.2,0,0.389,0.078,0.53,0.22c0.142,0.142,0.22,0.33,0.22,0.53s-0.078,0.389-0.22,0.53
        l-3.157,3.157c0.001,0.021,0.002,0.042,0.002,0.062C5.534,22.054,5.029,22.559,4.409,22.559z"/>
      <path d="M16.409,22.559c-0.62,0-1.125-0.505-1.125-1.125s0.505-1.125,1.125-1.125c0.021,0,0.042,0.001,0.062,0.002l3.157-3.157
        c0.142-0.142,0.33-0.22,0.53-0.22s0.389,0.078,0.53,0.22s0.22,0.33,0.22,0.53s-0.078,0.389-0.22,0.53l-3.157,3.157
        c0.001,0.021,0.002,0.042,0.002,0.062C17.534,22.054,17.029,22.559,16.409,22.559z"/>
        `;
        break;

      case 'storms-2':
        this.svgPaths = `
        <path d="M17.354,20.131c-2.496,0-4.898-0.977-6.828-1.761c-1.506-0.612-2.804-1.141-3.776-1.141c0,0-0.029,0-0.029,0
        c-2.033,0-3.969,0.732-5.474,2.062c-0.137,0.121-0.314,0.188-0.497,0.188c-0.215,0-0.42-0.093-0.562-0.254
        c-0.132-0.15-0.199-0.343-0.186-0.543c0.012-0.2,0.102-0.383,0.252-0.516c0.389-0.344,0.808-0.657,1.246-0.935V13.13l-0.334,0.223
        c-0.124,0.083-0.267,0.126-0.416,0.126c-0.251,0-0.485-0.125-0.624-0.334c-0.229-0.344-0.136-0.811,0.208-1.04l4.5-3
        C4.957,9.022,5.101,8.979,5.25,8.979s0.292,0.044,0.416,0.126l4.5,3c0.344,0.229,0.437,0.696,0.208,1.04
        c-0.139,0.209-0.373,0.334-0.624,0.334c-0.149,0-0.292-0.044-0.416-0.126L9,13.13v3.047c0.673,0.226,1.392,0.518,2.089,0.802
        c1.91,0.777,4.073,1.656,6.252,1.656c2.035,0,3.794-0.77,5.378-2.354c0.142-0.142,0.33-0.22,0.53-0.22s0.389,0.078,0.53,0.22
        c0.292,0.292,0.292,0.768,0,1.061C21.904,19.219,19.802,20.131,17.354,20.131z M3,16.458c1.168-0.478,2.434-0.729,3.696-0.729
        l0.056,0c0.231,0,0.478,0.021,0.748,0.064V12.13l-2.25-1.5L3,12.13V16.458z"/>
        <path d="M23.249,13.062c-0.151,0-0.298-0.045-0.423-0.131c-1.64-1.122-2.438-3.189-3.283-5.378
          c-1.206-3.124-2.344-6.074-5.859-6.074c-2.214,0-4.005,1.734-4.078,3.947c-0.005,0.144,0.05,0.287,0.15,0.391
          c0.011,0.012,0.023,0.023,0.035,0.033c0.099-0.539,0.364-1.033,0.769-1.419c0.491-0.469,1.132-0.727,1.804-0.727l0.071,0.001
          c0.7,0.016,1.353,0.304,1.837,0.811c0.484,0.507,0.742,1.172,0.726,1.873c0.054,1.594-0.391,3.167-1.288,4.529
          c-0.139,0.211-0.373,0.338-0.626,0.338c-0.147,0-0.289-0.043-0.412-0.124c-0.345-0.228-0.441-0.693-0.214-1.039
          c0.725-1.101,1.085-2.379,1.041-3.697c0.007-0.34-0.101-0.625-0.311-0.845C12.98,5.334,12.701,5.211,12.4,5.204l-0.026,0
          c-0.606,0-1.11,0.493-1.124,1.099v0.054c0,0.472-0.257,0.865-0.671,1.033c-0.147,0.06-0.31,0.09-0.485,0.09c0,0,0,0,0,0
          c-0.487,0-1.056-0.249-1.415-0.62C8.298,6.464,8.09,5.923,8.108,5.376c0.099-3.026,2.548-5.397,5.577-5.397
          c4.544,0,6.049,3.9,7.258,7.034c0.758,1.965,1.475,3.821,2.73,4.68c0.342,0.234,0.429,0.701,0.196,1.042
          C23.729,12.94,23.497,13.062,23.249,13.062z"/>
        <path d="M17.354,23.881c-2.496,0-4.898-0.977-6.828-1.761c-1.506-0.612-2.804-1.141-3.776-1.141c0,0-0.029,0-0.029,0
          c-2.033,0-3.969,0.732-5.474,2.062c-0.137,0.121-0.314,0.188-0.497,0.188c-0.215,0-0.42-0.092-0.562-0.253
          c-0.133-0.15-0.199-0.343-0.186-0.543c0.012-0.2,0.102-0.383,0.252-0.516c1.78-1.572,4.068-2.438,6.443-2.438l0.056,0
          c1.263,0,2.687,0.579,4.336,1.25c1.911,0.777,4.075,1.656,6.253,1.656c2.035,0,3.794-0.77,5.378-2.354
          c0.142-0.142,0.33-0.22,0.53-0.22s0.389,0.078,0.53,0.22c0.292,0.292,0.292,0.768,0,1.061
          C21.904,22.969,19.802,23.881,17.354,23.881z"/>`;
        break;

      /// JOB CARD

      case 'job-card-notes':
        this.svgPaths = `
        <path d="M2.25,24C1.009,24,0,22.991,0,21.75V2.25C0,1.009,1.009,0,2.25,0h19.5C22.991,0,24,1.009,24,2.25v11.379
        c0,0.317-0.066,0.625-0.196,0.916c-0.002,0.005-0.009,0.018-0.011,0.023c-0.109,0.239-0.262,0.461-0.451,0.65l-8.123,8.122
        c-0.19,0.19-0.411,0.343-0.659,0.456c-0.006,0.003-0.026,0.012-0.032,0.014C14.254,23.934,13.946,24,13.629,24H2.25z M2.25,1.5
        C1.836,1.5,1.5,1.836,1.5,2.25v19.5c0,0.414,0.336,0.75,0.75,0.75H13.5v-6.75c0-1.241,1.009-2.25,2.25-2.25h6.75V2.25
        c0-0.414-0.336-0.75-0.75-0.75H2.25z M15.75,15C15.336,15,15,15.336,15,15.75v5.689L21.439,15H15.75z"/>`;
        break;

      case 'more-horizontal':
        this.svgPaths = `
        <circle cx="3.25" cy="12" r="3.25"/><circle cx="12" cy="12" r="3.25"/><circle cx="20.75" cy="12" r="3.25"/>`;
        break;

      case 'more-vertical':
        this.svgPaths = `
        <circle cx="12" cy="3.25" r="3.25"/><circle cx="12" cy="12" r="3.25"/><circle cx="12" cy="20.75" r="3.25"/>`;
        break;

      /// JOB CARD
      case 'SLA-clock-quarter':
        this.svgPaths = `
        <path d="M11.25,16.25c-0.414,0-0.75-0.336-0.75-0.75V2.75H9C8.586,2.75,8.25,2.414,8.25,2S8.586,1.25,9,1.25h4.5
          c0.414,0,0.75,0.336,0.75,0.75s-0.336,0.75-0.75,0.75H12v3.031c1.893,0.144,3.67,0.824,5.177,1.982l0.911-0.911
          C18.03,6.745,18,6.625,18,6.5c0-0.2,0.078-0.389,0.22-0.53s0.33-0.22,0.53-0.22s0.389,0.078,0.53,0.22l0.75,0.75
          c0.142,0.141,0.22,0.33,0.22,0.53s-0.078,0.389-0.22,0.53S19.7,8,19.5,8c-0.125,0-0.245-0.03-0.352-0.087l-0.855,0.855
          C20.042,10.592,21,12.968,21,15.5c0,0.414-0.336,0.75-0.75,0.75H11.25z M19.466,14.75C19.107,10.821,15.929,7.643,12,7.284v7.466
          H19.466z"/>
        <circle cx="11.25" cy="21.875" r="1.125"/>
        <circle cx="4.875" cy="15.5" r="1.125"/>
        <path d="M6.715,21.142c-0.303-0.01-0.582-0.138-0.787-0.358c-0.194-0.192-0.31-0.478-0.311-0.775c0-0.62,0.504-1.125,1.124-1.126
          c0.302,0,0.584,0.117,0.797,0.33l0.023,0.023c0.434,0.466,0.411,1.181-0.043,1.604c-0.209,0.195-0.481,0.302-0.766,0.302
          L6.715,21.142z"/>
        <path d="M15.758,21.133c-0.302,0-0.584-0.117-0.797-0.331c-0.208-0.208-0.328-0.498-0.328-0.793c0-0.3,0.116-0.583,0.329-0.795
          c0.212-0.212,0.494-0.33,0.795-0.33c0.3,0,0.583,0.117,0.795,0.328c0.213,0.213,0.331,0.496,0.331,0.796
          c0,0.293-0.112,0.571-0.316,0.782C16.339,21.017,16.058,21.133,15.758,21.133z"/>
        <path d="M6.74,12.119c-0.622-0.001-1.126-0.507-1.125-1.128c0.001-0.62,0.507-1.124,1.126-1.124c0.297,0,0.588,0.121,0.797,0.331
          c0.212,0.212,0.329,0.495,0.329,0.797c-0.001,0.62-0.507,1.124-1.126,1.124H6.74z"/>
        <path d="M15.227,12.647c-0.3,0-0.583-0.117-0.795-0.328c-0.212-0.212-0.33-0.494-0.33-0.795c0-0.301,0.116-0.583,0.329-0.796
          c0.208-0.21,0.498-0.33,0.795-0.33c0.305,0.003,0.586,0.12,0.796,0.329c0.439,0.439,0.439,1.152,0.001,1.591
          C15.811,12.531,15.528,12.647,15.227,12.647z"/>
        `;
        break;

      case 'SLA-clock-half':
        this.svgPaths = `
        <path d="M10.75,24C10.336,24,10,23.664,10,23.25V1.5H8.5c-0.414,0-0.75-0.336-0.75-0.75S8.086,0,8.5,0H13
        c0.414,0,0.75,0.336,0.75,0.75S13.414,1.5,13,1.5h-1.5v3.031c1.893,0.144,3.67,0.824,5.177,1.982l0.911-0.911
        C17.53,5.495,17.5,5.375,17.5,5.25c0-0.2,0.078-0.389,0.22-0.53s0.33-0.22,0.53-0.22s0.389,0.078,0.53,0.22l0.75,0.75
        C19.672,5.611,19.75,5.8,19.75,6s-0.078,0.389-0.22,0.53S19.2,6.75,19,6.75c-0.125,0-0.245-0.03-0.352-0.087l-0.855,0.855
        c1.749,1.824,2.707,4.201,2.707,6.733C20.5,19.626,16.126,24,10.75,24z M11.5,22.466c4.236-0.38,7.5-3.916,7.5-8.216
        c0-4.3-3.264-7.837-7.5-8.216V22.466z"/>
        <circle cx="4.375" cy="14.25" r="1.125"/>
        <path d="M6.215,19.892c-0.303-0.01-0.582-0.138-0.787-0.358c-0.194-0.192-0.31-0.478-0.311-0.775c0-0.62,0.504-1.125,1.124-1.126
          c0.302,0,0.584,0.117,0.797,0.33l0.023,0.023c0.434,0.466,0.411,1.181-0.043,1.604c-0.209,0.195-0.481,0.302-0.766,0.302
          L6.215,19.892z"/>
        <path d="M6.242,10.865c-0.3,0-0.581-0.116-0.793-0.328c-0.213-0.212-0.33-0.494-0.331-0.794c0-0.3,0.116-0.583,0.328-0.796
          C5.65,8.743,5.94,8.626,6.242,8.626c0.301,0,0.59,0.116,0.793,0.319c0.439,0.437,0.44,1.15,0.003,1.589
          c-0.212,0.213-0.494,0.33-0.795,0.331C6.243,10.865,6.242,10.865,6.242,10.865z"/>
        <circle cx="16.375" cy="14.25" r="1.125"/>
        <path d="M14.727,19.353c-0.301,0-0.583-0.117-0.795-0.329c-0.438-0.438-0.439-1.151-0.002-1.59c0.208-0.21,0.499-0.331,0.795-0.331
          c0.301,0,0.583,0.117,0.796,0.328c0.439,0.439,0.44,1.153,0.002,1.591C15.311,19.235,15.028,19.353,14.727,19.353z"/>
        <path d="M14.727,11.4c-0.3,0-0.583-0.117-0.795-0.329c-0.212-0.212-0.33-0.494-0.33-0.795c0-0.301,0.116-0.583,0.329-0.796
          c0.208-0.209,0.498-0.33,0.795-0.33c0.302,0,0.583,0.117,0.796,0.328c0.439,0.438,0.439,1.152,0.001,1.591
          C15.311,11.282,15.028,11.4,14.727,11.4z"/>`;
        break;

      case 'SLA-clock-three-quarter':
        this.svgPaths = `
        <circle cx="12" cy="19.875" r="1.125"/>
        <circle cx="17.625" cy="14.25" r="1.125"/>
        <path d="M15.977,19.353c-0.301,0-0.583-0.117-0.795-0.329c-0.438-0.438-0.439-1.151-0.002-1.59c0.208-0.21,0.499-0.331,0.795-0.331
          c0.301,0,0.583,0.117,0.796,0.328c0.439,0.439,0.44,1.153,0.002,1.591C16.561,19.235,16.278,19.353,15.977,19.353z"/>
        <path d="M8.022,19.353c-0.3,0-0.583-0.117-0.796-0.33c-0.21-0.209-0.329-0.5-0.329-0.797c0-0.3,0.118-0.582,0.331-0.794
          c0.212-0.212,0.494-0.328,0.794-0.328c0.298,0,0.588,0.121,0.797,0.331c0.212,0.212,0.329,0.495,0.328,0.796
          c0,0.301-0.118,0.583-0.331,0.795C8.605,19.236,8.323,19.353,8.022,19.353L8.022,19.353z"/>
        <path d="M15.977,11.4c-0.3,0-0.583-0.117-0.795-0.329c-0.212-0.212-0.33-0.494-0.33-0.795c0-0.301,0.116-0.583,0.329-0.796
          c0.208-0.209,0.498-0.33,0.795-0.33c0.302,0,0.583,0.117,0.796,0.328c0.439,0.438,0.439,1.152,0.001,1.591
          C16.561,11.282,16.278,11.4,15.977,11.4z"/>
        <path d="M12,24c-5.376,0-9.75-4.374-9.75-9.75c0-0.414,0.336-0.75,0.75-0.75h8.25v-12h-1.5C9.336,1.5,9,1.164,9,0.75
          S9.336,0,9.75,0h4.5C14.664,0,15,0.336,15,0.75S14.664,1.5,14.25,1.5h-1.5v3.031c1.893,0.144,3.67,0.824,5.177,1.982l0.911-0.911
          C18.78,5.495,18.75,5.375,18.75,5.25c0-0.2,0.078-0.389,0.22-0.53S19.3,4.5,19.5,4.5s0.389,0.078,0.53,0.22l0.75,0.75
          C20.922,5.611,21,5.8,21,6s-0.078,0.389-0.22,0.53s-0.33,0.22-0.53,0.22c-0.125,0-0.245-0.03-0.352-0.087l-0.855,0.855
          c1.749,1.824,2.707,4.201,2.707,6.733C21.75,19.626,17.376,24,12,24z M3.784,15C4.163,19.236,7.7,22.5,12,22.5
          c4.549,0,8.25-3.701,8.25-8.25c0-4.3-3.264-7.837-7.5-8.216v8.216c0,0.414-0.336,0.75-0.75,0.75H3.784z"/>
        <path d="M7.49,10.869c-0.622-0.001-1.126-0.507-1.125-1.128c0.001-0.62,0.507-1.124,1.126-1.124c0.297,0,0.587,0.12,0.797,0.331
          c0.213,0.213,0.33,0.496,0.329,0.797c-0.001,0.62-0.507,1.124-1.126,1.124H7.49z"/>`;
        break;

      case 'SLA-clock-full':
        this.svgPaths = `
        <path d="M12,24c-5.376,0-9.75-4.374-9.75-9.75c0-5.133,3.925-9.335,9-9.719V1.5h-1.5C9.336,1.5,9,1.164,9,0.75S9.336,0,9.75,0h4.5
          C14.664,0,15,0.336,15,0.75S14.664,1.5,14.25,1.5h-1.5v3.031c1.893,0.144,3.67,0.824,5.177,1.982l0.911-0.911
          C18.78,5.495,18.75,5.375,18.75,5.25c0-0.2,0.078-0.389,0.22-0.53S19.3,4.5,19.5,4.5s0.389,0.078,0.53,0.22l0.75,0.75
          C20.922,5.611,21,5.8,21,6s-0.078,0.389-0.22,0.53s-0.33,0.22-0.53,0.22c-0.125,0-0.245-0.03-0.352-0.087l-0.855,0.855
          c1.749,1.824,2.707,4.201,2.707,6.733C21.75,19.626,17.376,24,12,24z M12,6c-4.549,0-8.25,3.701-8.25,8.25S7.451,22.5,12,22.5
          s8.25-3.701,8.25-8.25S16.549,6,12,6z"/>
        <path d="M12,9.75c-0.62,0-1.125-0.505-1.125-1.125c0-0.599,0.468-1.09,1.067-1.121c0,0,0.02-0.002,0.03-0.003
          c0,0-0.014,0.001-0.026,0.002C11.963,7.501,11.984,7.5,12,7.5c0.62,0,1.125,0.505,1.125,1.125S12.62,9.75,12,9.75z"/>
        <circle cx="12" cy="19.875" r="1.125"/>
        <circle cx="6.375" cy="14.25" r="1.125"/>
        <circle cx="17.625" cy="14.25" r="1.125"/>
        <path d="M8.022,11.4c-0.301,0-0.583-0.118-0.796-0.33c-0.213-0.213-0.33-0.496-0.329-0.797c0-0.3,0.118-0.582,0.331-0.794
          C7.441,9.267,7.723,9.15,8.022,9.15c0.298,0,0.589,0.121,0.798,0.331c0.211,0.212,0.328,0.494,0.328,0.795
          c0,0.3-0.118,0.583-0.33,0.795C8.605,11.283,8.323,11.4,8.022,11.4L8.022,11.4z"/>
        <path d="M15.977,19.353c-0.301,0-0.583-0.117-0.795-0.329c-0.438-0.438-0.439-1.151-0.002-1.59c0.208-0.21,0.499-0.331,0.795-0.331
          c0.301,0,0.583,0.117,0.796,0.328c0.439,0.439,0.44,1.153,0.002,1.591C16.561,19.235,16.278,19.353,15.977,19.353z"/>
        <path d="M8.022,19.353c-0.3,0-0.583-0.117-0.796-0.33c-0.21-0.209-0.329-0.5-0.329-0.797c0-0.3,0.118-0.582,0.331-0.794
          c0.212-0.212,0.494-0.328,0.794-0.328c0.298,0,0.588,0.121,0.797,0.331c0.212,0.212,0.329,0.495,0.328,0.796
          c0,0.301-0.118,0.583-0.331,0.795C8.605,19.236,8.323,19.353,8.022,19.353L8.022,19.353z"/>
        <path d="M15.978,11.397c-0.3,0-0.582-0.116-0.794-0.328c-0.213-0.212-0.33-0.494-0.331-0.795c-0.001-0.3,0.116-0.583,0.329-0.796
          c0.208-0.21,0.498-0.33,0.795-0.33c0.3,0,0.589,0.121,0.799,0.331c0.436,0.438,0.435,1.15-0.001,1.588
          C16.562,11.28,16.279,11.397,15.978,11.397z"/>`;
        break;

      case 'offline':
        this.svgPaths = `
          <path d="M0.75,24c-0.2,0-0.389-0.078-0.53-0.22c-0.292-0.292-0.292-0.768,0-1.061l22.5-22.5C22.861,0.078,23.05,0,23.25,0
          s0.389,0.078,0.53,0.22C23.922,0.361,24,0.55,24,0.75s-0.078,0.389-0.22,0.53l-22.5,22.5C1.139,23.922,0.95,24,0.75,24z"/>
        <path d="M12,24c-2.068,0-3.75-1.682-3.75-3.75S9.932,16.5,12,16.5c2.068,0,3.75,1.682,3.75,3.75S14.068,24,12,24z M12,18
          c-1.241,0-2.25,1.009-2.25,2.25S10.759,22.5,12,22.5c1.241,0,2.25-1.009,2.25-2.25S13.241,18,12,18z"/>
        <path d="M17.3,15.697c-0.2,0-0.389-0.078-0.53-0.22c-0.587-0.587-1.269-1.055-2.027-1.392c-0.377-0.168-0.548-0.612-0.381-0.99
          c0.121-0.271,0.39-0.446,0.686-0.446c0.105,0,0.208,0.022,0.304,0.065c0.927,0.412,1.761,0.985,2.479,1.702
          c0.142,0.142,0.22,0.33,0.22,0.53s-0.078,0.389-0.22,0.53S17.5,15.697,17.3,15.697z"/>
        <path d="M3.515,12.515c-0.2,0-0.388-0.078-0.529-0.219c-0.293-0.292-0.294-0.768-0.002-1.061C5.18,9.032,8.1,7.714,11.205,7.524
          c0.008,0,0.039-0.001,0.047-0.001c0.395,0,0.723,0.309,0.747,0.705c0.012,0.2-0.054,0.392-0.187,0.542
          c-0.133,0.15-0.316,0.239-0.516,0.251c-2.739,0.167-5.313,1.33-7.25,3.273C3.905,12.437,3.716,12.515,3.515,12.515z"/>
        <path d="M20.485,12.515c-0.201,0-0.389-0.078-0.531-0.22c-0.706-0.707-1.504-1.318-2.372-1.815
          c-0.359-0.206-0.483-0.665-0.278-1.023c0.133-0.233,0.383-0.377,0.651-0.377c0.13,0,0.259,0.034,0.372,0.099
          c0.984,0.564,1.888,1.256,2.688,2.057c0.142,0.142,0.219,0.33,0.219,0.531c0,0.2-0.078,0.389-0.22,0.53
          C20.873,12.437,20.685,12.515,20.485,12.515z"/>
        <path d="M0.75,8.931c-0.207,0-0.407-0.087-0.548-0.238C0.065,8.546-0.006,8.355,0.001,8.155c0.007-0.2,0.091-0.386,0.238-0.522
          c3.2-2.986,7.386-4.631,11.786-4.631c1.026,0,2.057,0.093,3.062,0.276c0.197,0.036,0.368,0.146,0.482,0.311
          c0.114,0.165,0.157,0.364,0.121,0.561c-0.065,0.357-0.375,0.615-0.739,0.615c-0.044,0-0.088-0.004-0.133-0.012
          C13.9,4.587,12.96,4.502,12.023,4.502c-4.018,0-7.84,1.501-10.761,4.228C1.123,8.859,0.941,8.931,0.75,8.931z"/>
        <path d="M23.25,8.931c-0.191,0-0.373-0.072-0.512-0.202c-0.716-0.668-1.499-1.272-2.326-1.795
          c-0.169-0.107-0.287-0.273-0.331-0.469s-0.01-0.396,0.097-0.566c0.139-0.219,0.376-0.349,0.635-0.349c0.142,0,0.281,0.04,0.4,0.116
          c0.907,0.573,1.765,1.235,2.549,1.966C23.908,7.769,23.993,7.955,24,8.155c0.007,0.2-0.064,0.391-0.201,0.538
          C23.657,8.844,23.457,8.931,23.25,8.931z"/>
        `;
        break;
      case 'online':
        this.svgPaths = `
          <path d="M12,22.53c-2.068,0-3.75-1.682-3.75-3.75s1.682-3.75,3.75-3.75c2.068,0,3.75,1.682,3.75,3.75S14.068,22.53,12,22.53z
          M12,16.53c-1.241,0-2.25,1.009-2.25,2.25s1.009,2.25,2.25,2.25c1.241,0,2.25-1.009,2.25-2.25S13.241,16.53,12,16.53z"/>
          <path d="M17.3,14.227c-0.2,0-0.389-0.078-0.53-0.22c-1.272-1.272-2.968-1.973-4.773-1.973s-3.5,0.701-4.773,1.973
            c-0.142,0.142-0.33,0.22-0.53,0.22s-0.389-0.078-0.53-0.22c-0.142-0.142-0.22-0.33-0.22-0.53s0.078-0.389,0.22-0.53
            c1.556-1.556,3.627-2.412,5.833-2.412c2.206,0,4.278,0.857,5.833,2.412c0.142,0.142,0.22,0.33,0.22,0.53s-0.078,0.389-0.22,0.53
            S17.5,14.227,17.3,14.227z"/>
          <path d="M20.485,11.045c-0.2,0-0.389-0.078-0.53-0.22C17.833,8.705,15.008,7.537,12,7.537c-3.008,0-5.833,1.168-7.955,3.289
            c-0.142,0.142-0.33,0.22-0.53,0.22s-0.389-0.078-0.53-0.22c-0.142-0.142-0.22-0.33-0.22-0.53c0-0.2,0.078-0.389,0.22-0.53
            C5.389,7.361,8.591,6.037,12,6.037c3.409,0,6.611,1.324,9.015,3.728c0.142,0.141,0.22,0.33,0.22,0.53c0,0.2-0.078,0.389-0.22,0.53
            C20.874,10.967,20.685,11.045,20.485,11.045z"/>
          <path d="M0.75,7.462c-0.207,0-0.407-0.087-0.549-0.239c-0.282-0.302-0.265-0.778,0.037-1.06C3.429,3.19,7.606,1.552,12,1.552
            c4.395,0,8.571,1.638,11.761,4.611c0.302,0.282,0.319,0.757,0.038,1.06c-0.142,0.152-0.342,0.239-0.549,0.239
            c-0.19,0-0.372-0.072-0.511-0.201C19.827,4.546,16.013,3.051,12,3.051S4.173,4.546,1.261,7.26C1.122,7.39,0.94,7.462,0.75,7.462z"
        />
        `;
        break;
      case 'flowchart':
        this.svgPaths = `
            <defs><style>.a{fill:none;stroke:currentColor;stroke-linecap:round;stroke-linejoin:round;stroke-width:1.5px;}</style></defs><title>car-dashboard-gear</title><circle class="a" cx="3" cy="3" r="2.25"/><circle class="a" cx="12" cy="3" r="2.25"/><circle class="a" cx="12" cy="12" r="2.25"/><circle class="a" cx="12" cy="21" r="2.25"/><line class="a" x1="5.25" y1="3" x2="9.75" y2="3"/><circle class="a" cx="3" cy="21" r="2.25"/><line class="a" x1="5.25" y1="21" x2="9.75" y2="21"/><circle class="a" cx="21" cy="12" r="2.25"/><line class="a" x1="14.25" y1="12" x2="18.75" y2="12"/><line class="a" x1="12" y1="14.25" x2="12" y2="18.75"/><line class="a" x1="12" y1="5.25" x2="12" y2="9.75"/>
        `;
        break;
      case 'view':
        this.svgPaths = `<defs><style>.a{fill:none;stroke:currentColor;stroke-linecap:round;stroke-linejoin:round;stroke-width:1.5px;}</style></defs><title>view-1</title><path class="a" d="M12,5.251C7.969,5.183,3.8,8,1.179,10.885a1.663,1.663,0,0,0,0,2.226C3.743,15.935,7.9,18.817,12,18.748c4.1.069,8.258-2.813,10.824-5.637a1.663,1.663,0,0,0,0-2.226C20.2,8,16.031,5.183,12,5.251Z"/><path class="a" d="M15.75,12A3.75,3.75,0,1,1,12,8.249,3.749,3.749,0,0,1,15.75,12Z"/>`;
        break;
      case 'documents-files':
        this.svgPaths = `
        <path d="M2.247,23.998c-1.241,0-2.25-1.009-2.25-2.25v-19.5c0-1.241,1.009-2.25,2.25-2.25h10.629c0.602,0,1.166,0.234,1.591,0.658
          l5.871,5.872c0.425,0.425,0.659,0.99,0.659,1.59v4.629c0,0.414-0.336,0.75-0.75,0.75s-0.75-0.336-0.75-0.75v-3.75h-5.25
          c-1.241,0-2.25-1.009-2.25-2.25v-5.25h-9.75c-0.414,0-0.75,0.336-0.75,0.75v19.5c0,0.414,0.336,0.75,0.75,0.75h3
          c0.414,0,0.75,0.336,0.75,0.75s-0.336,0.75-0.75,0.75H2.247z M13.497,6.748c0,0.414,0.336,0.75,0.75,0.75h4.939l-5.689-5.69V6.748z
          "/>
        <path d="M8.247,23.998c-0.414,0-0.75-0.336-0.75-0.75v-7.5c0-0.414,0.336-0.75,0.75-0.75c2.068,0,3.75,1.682,3.75,3.75v1.5
          C11.997,22.316,10.315,23.998,8.247,23.998z M8.997,22.369c0.879-0.313,1.5-1.159,1.5-2.121v-1.5c0-0.962-0.621-1.808-1.5-2.121
          V22.369z"/>
        <path d="M15.747,23.998c-1.241,0-2.25-1.009-2.25-2.25v-4.5c0-1.241,1.009-2.25,2.25-2.25s2.25,1.009,2.25,2.25v4.5
          C17.997,22.989,16.988,23.998,15.747,23.998z M15.747,16.498c-0.414,0-0.75,0.336-0.75,0.75v4.5c0,0.414,0.336,0.75,0.75,0.75
          s0.75-0.336,0.75-0.75v-4.5C16.497,16.834,16.161,16.498,15.747,16.498z"/>
        <path d="M23.247,23.998c-2.068,0-3.75-1.682-3.75-3.75v-1.5c0-2.068,1.682-3.75,3.75-3.75c0.414,0,0.75,0.336,0.75,0.75
          s-0.336,0.75-0.75,0.75c-1.241,0-2.25,1.009-2.25,2.25v1.5c0,1.241,1.009,2.25,2.25,2.25c0.414,0,0.75,0.336,0.75,0.75
          S23.661,23.998,23.247,23.998z"/>
        `;
        break;

      case 'suggested-fix':
        this.svgPaths = `
          <path d="M8.307,11.167c-0.2,0-0.388-0.078-0.53-0.219l-0.693-0.69c-0.576,0.208-1.175,0.313-1.786,0.313c0,0,0,0,0,0
            c-0.598,0-1.19-0.102-1.761-0.303c-1.333-0.47-2.404-1.43-3.016-2.705c-0.694-1.448-0.696-3.111-0.007-4.561
            c0.104-0.22,0.31-0.375,0.549-0.417c0.044-0.007,0.086-0.011,0.127-0.011c0.2,0,0.389,0.078,0.53,0.219l1.425,1.421
            c0.15,0.154,0.338,0.235,0.545,0.237l0.039,0.001c0.232,0,0.402-0.119,0.504-0.218c0.146-0.142,0.227-0.333,0.23-0.538
            C4.467,3.492,4.39,3.3,4.248,3.154L2.808,1.719c-0.172-0.172-0.251-0.417-0.21-0.657c0.041-0.24,0.196-0.446,0.416-0.55
            c0.722-0.345,1.489-0.52,2.279-0.52c0.599,0,1.193,0.102,1.765,0.304c1.333,0.47,2.403,1.432,3.013,2.707
            c0.608,1.271,0.682,2.735,0.213,4.055l0.615,0.613c0.293,0.292,0.293,0.768,0.002,1.061c-0.142,0.142-0.331,0.22-0.531,0.22
            c-0.2,0-0.388-0.078-0.529-0.218L8.87,7.766C8.646,7.542,8.586,7.199,8.722,6.913c0.493-1.038,0.492-2.227-0.004-3.264
            C8.281,2.735,7.515,2.046,6.56,1.709C6.149,1.564,5.724,1.491,5.295,1.491c-0.184,0-0.368,0.014-0.55,0.041l0.574,0.572
            C5.744,2.539,5.972,3.11,5.965,3.714C5.958,4.319,5.715,4.885,5.283,5.307C4.857,5.723,4.295,5.952,3.7,5.952
            c-0.001,0-0.024,0-0.025,0C3.07,5.944,2.504,5.702,2.081,5.27L1.54,4.73C1.432,5.469,1.545,6.226,1.875,6.915
            C2.313,7.828,3.08,8.517,4.036,8.853c0.41,0.144,0.835,0.218,1.263,0.218c0.567,0,1.117-0.126,1.636-0.375
            c0.101-0.048,0.213-0.074,0.324-0.074c0.2,0,0.388,0.078,0.529,0.219l1.048,1.044c0.142,0.141,0.22,0.329,0.221,0.53
            c0,0.2-0.077,0.389-0.219,0.531C8.697,11.088,8.508,11.167,8.307,11.167z"/>
          <path d="M18.706,23.929c-0.599,0-1.193-0.102-1.765-0.304c-1.333-0.471-2.403-1.433-3.012-2.708
            c-0.608-1.271-0.682-2.734-0.213-4.053L12.648,15.8c-0.293-0.292-0.293-0.768-0.001-1.061c0.142-0.142,0.331-0.22,0.531-0.22
            c0.2,0,0.388,0.078,0.53,0.219l1.422,1.418c0.224,0.224,0.283,0.567,0.148,0.853c-0.493,1.037-0.491,2.226,0.004,3.262
            c0.437,0.914,1.203,1.603,2.158,1.94c0.411,0.145,0.836,0.218,1.265,0.218c0.184,0,0.367-0.013,0.549-0.041l-0.546-0.544
            c-0.86-0.88-0.859-2.303-0.003-3.176c0.428-0.436,1.002-0.677,1.615-0.677c0.598,0,1.161,0.23,1.586,0.648l0.551,0.549
            c0.108-0.738-0.006-1.494-0.335-2.181c-0.627-1.309-1.972-2.154-3.428-2.154c-0.567,0-1.116,0.125-1.632,0.372
            c-0.1,0.048-0.212,0.073-0.323,0.073c-0.199,0-0.387-0.077-0.529-0.218l-1.423-1.419c-0.293-0.292-0.293-0.768-0.001-1.061
            c0.142-0.142,0.331-0.22,0.531-0.22c0.2,0,0.388,0.078,0.529,0.219l1.067,1.064c0.572-0.206,1.17-0.31,1.78-0.31
            c2.03,0,3.907,1.18,4.781,3.007c0.693,1.447,0.696,3.107,0.007,4.556c-0.104,0.22-0.309,0.376-0.549,0.417
            c-0.041,0.008-0.085,0.012-0.13,0.012c-0.198,0-0.385-0.078-0.527-0.219l-1.425-1.42c-0.14-0.138-0.33-0.215-0.531-0.215
            c-0.208,0-0.402,0.081-0.546,0.229c-0.289,0.294-0.29,0.774-0.001,1.069l1.417,1.413c0.172,0.172,0.25,0.418,0.209,0.657
            c-0.041,0.24-0.196,0.446-0.416,0.551C20.264,23.754,19.497,23.929,18.706,23.929z"/>
          <path d="M3.033,23.998c-0.8,0-1.552-0.31-2.117-0.872c-1.166-1.16-1.172-3.052-0.012-4.219l4.706-4.697
            c0.283-0.282,0.659-0.437,1.059-0.437c0.4,0,0.776,0.155,1.059,0.437l0.438,0.436l6.29-6.083l-2.635-2.626
            c-0.875-0.87-0.878-2.291-0.008-3.166l2.124-2.118c0.424-0.423,0.988-0.656,1.59-0.656c0.6,0,1.164,0.232,1.587,0.654l6.221,6.2
            c0.424,0.422,0.658,0.983,0.659,1.581c0.002,0.598-0.23,1.161-0.652,1.585c-0.008,0.008-0.021,0.02-0.031,0.03l-2.093,2.09
            c-0.424,0.423-0.988,0.656-1.589,0.656s-1.165-0.233-1.588-0.655l-2.523-2.515l-6.29,6.083l0.618,0.616
            c0.283,0.283,0.439,0.658,0.439,1.058c0,0.399-0.155,0.775-0.437,1.058c-0.006,0.006-0.029,0.028-0.035,0.033l-4.663,4.655
            C4.586,23.688,3.834,23.998,3.033,23.998L3.033,23.998z M1.975,19.958c-0.58,0.583-0.581,1.528-0.001,2.104
            c0.282,0.281,0.658,0.436,1.059,0.436s0.777-0.155,1.059-0.436c0,0,4.695-4.685,4.695-4.685c0,0-1.162-1.152-1.164-1.154
            l-0.954-0.951L1.975,19.958z M15.528,1.497c-0.2,0-0.389,0.078-0.53,0.218l-2.117,2.111c-0.14,0.141-0.218,0.328-0.218,0.527
            c0,0.198,0.077,0.383,0.217,0.522l6.221,6.2c0.141,0.141,0.329,0.218,0.53,0.218s0.389-0.077,0.53-0.218l2.116-2.113
            c0.143-0.144,0.219-0.326,0.219-0.526c-0.001-0.197-0.078-0.383-0.218-0.522l-6.221-6.2C15.916,1.574,15.728,1.497,15.528,1.497z"
            />
          `;
        break;

      case 'private-client':
        this.svgPaths = `
        <g transform="matrix(1,0,0,1,-4.28997,1.17556)">
          <g transform="matrix(12,0,0,12,5.65797,14.9044)">
              <path d="M0.324,-0.198L0.212,-0.198L0.212,0L0.074,0L0.074,-0.68L0.324,-0.68C0.485,-0.68 0.566,-0.601 0.566,-0.444C0.566,-0.365 0.546,-0.304 0.505,-0.262C0.464,-0.219 0.403,-0.198 0.324,-0.198ZM0.212,-0.316L0.323,-0.316C0.392,-0.316 0.426,-0.359 0.426,-0.444C0.426,-0.486 0.418,-0.516 0.401,-0.535C0.384,-0.553 0.358,-0.562 0.323,-0.562L0.212,-0.562L0.212,-0.316Z" style="fill-rule:nonzero;"/>
          </g>
          <g transform="matrix(12,0,0,12,12.642,14.9044)">
              <path d="M0.442,-0.68L0.587,-0.68L0.429,0L0.174,0L0.016,-0.68L0.161,-0.68L0.279,-0.12L0.324,-0.12L0.442,-0.68Z" style="fill-rule:nonzero;"/>
          </g>
          <g transform="matrix(12,0,0,12,19.878,14.9044)">
              <path d="M0.013,-0.558L0.013,-0.68L0.513,-0.68L0.513,-0.558L0.333,-0.558L0.333,0L0.195,0L0.195,-0.558L0.013,-0.558Z" style="fill-rule:nonzero;"/>
          </g>
        </g>
          `;
        break;
      case 'excess-details':
        this.svgPaths = `
        <path d="M17.25,21.761c-0.414,0-0.75-0.336-0.75-0.75h-0.75c-0.414,0-0.75-0.336-0.75-0.75s0.336-0.75,0.75-0.75h2.031
          c0.242,0,0.458-0.146,0.548-0.371c0.059-0.146,0.057-0.307-0.005-0.453c-0.062-0.146-0.177-0.259-0.323-0.317l-2.063-0.825
          c-0.797-0.321-1.312-1.082-1.312-1.94c0-0.559,0.217-1.084,0.612-1.48c0.344-0.344,0.786-0.554,1.262-0.603v-0.011
          c0-0.414,0.336-0.75,0.75-0.75S18,13.097,18,13.511h0.75c0.414,0,0.75,0.336,0.75,0.75s-0.336,0.75-0.75,0.75h-2.033
          c-0.326,0-0.591,0.266-0.591,0.593c0,0.243,0.146,0.458,0.371,0.549l2.063,0.825c1.071,0.431,1.592,1.652,1.161,2.722
          C19.428,20.428,18.767,20.92,18,21v0.011C18,21.424,17.664,21.761,17.25,21.761z"/>
        <path d="M17.25,24.011c-3.722,0-6.75-3.028-6.75-6.75s3.028-6.75,6.75-6.75S24,13.539,24,17.261S20.972,24.011,17.25,24.011z
          M17.25,12.011c-2.895,0-5.25,2.355-5.25,5.25s2.355,5.25,5.25,5.25s5.25-2.355,5.25-5.25S20.145,12.011,17.25,12.011z"/>
        <path d="M5.25,11.261c-0.414,0-0.75-0.336-0.75-0.75s0.336-0.75,0.75-0.75h5.25c0.414,0,0.75,0.336,0.75,0.75
          s-0.336,0.75-0.75,0.75H5.25z"/>
        <path d="M5.25,15.011c-0.414,0-0.75-0.336-0.75-0.75s0.336-0.75,0.75-0.75h3c0.414,0,0.75,0.336,0.75,0.75s-0.336,0.75-0.75,0.75
          H5.25z"/>
        <path d="M5.25,18.761c-0.414,0-0.75-0.336-0.75-0.75s0.336-0.75,0.75-0.75h3c0.414,0,0.75,0.336,0.75,0.75s-0.336,0.75-0.75,0.75
          H5.25z"/>
        <path d="M2.25,24.011c-1.241,0-2.25-1.009-2.25-2.25V6.011c0-1.241,1.009-2.25,2.25-2.25h3.063c0.362-2.129,2.248-3.75,4.437-3.75
          c2.19,0,4.076,1.621,4.437,3.75h3.063c1.241,0,2.25,1.009,2.25,2.25v2.25c0,0.414-0.336,0.75-0.75,0.75S18,8.675,18,8.261v-2.25
          c0-0.414-0.336-0.75-0.75-0.75H13.5c-0.414,0-0.75-0.336-0.75-0.75c0-1.654-1.346-3-3-3s-3,1.346-3,3c0,0.414-0.336,0.75-0.75,0.75
          H2.25c-0.414,0-0.75,0.336-0.75,0.75v15.75c0,0.414,0.336,0.75,0.75,0.75h7.5c0.414,0,0.75,0.336,0.75,0.75s-0.336,0.75-0.75,0.75
          H2.25z"/>
        <circle cx="9.75" cy="4.136" r="1.125"/>
        `;
        break;
      case 'timeline':
        this.svgPaths = `
        <circle cx="5.251" cy="5.25" r="1.125"/>
        <path d="M10.501,6c-0.414,0-0.75-0.336-0.75-0.75s0.336-0.75,0.75-0.75h6.75c0.414,0,0.75,0.336,0.75,0.75S17.665,6,17.251,6
          H10.501z"/>
        <circle cx="5.251" cy="14.25" r="1.125"/>
        <path d="M5.251,19.5c-2.895,0-5.25-2.355-5.25-5.25c0-1.865,0.979-3.56,2.548-4.5C0.98,8.81,0.001,7.115,0.001,5.25
          C0.001,2.355,2.356,0,5.251,0h12c1.405,0,2.722,0.545,3.713,1.535c0.992,0.991,1.54,2.309,1.541,3.711
          c0.001,1.467-0.617,2.877-1.697,3.869c-0.138,0.127-0.319,0.198-0.507,0.198c-0.209,0-0.411-0.088-0.552-0.243
          c-0.28-0.304-0.26-0.78,0.045-1.06c0.771-0.709,1.213-1.716,1.212-2.764c-0.001-1.002-0.392-1.943-1.1-2.651
          C19.196,1.889,18.255,1.5,17.255,1.5H5.251c-2.068,0-3.75,1.682-3.75,3.75S3.183,9,5.251,9h6.685c0.414,0,0.75,0.336,0.75,0.75
          s-0.336,0.75-0.75,0.75H5.251c-2.068,0-3.75,1.682-3.75,3.75c0,2.068,1.682,3.75,3.75,3.75h3c0.414,0,0.75,0.336,0.75,0.75
          s-0.336,0.75-0.75,0.75H5.251z"/>
        <path d="M17.251,24c-3.722,0-6.75-3.028-6.75-6.75s3.028-6.75,6.75-6.75s6.75,3.028,6.75,6.75S20.973,24,17.251,24z M17.251,12
          c-2.895,0-5.25,2.355-5.25,5.25s2.355,5.25,5.25,5.25s5.25-2.355,5.25-5.25S20.146,12,17.251,12z"/>
        <path d="M17.251,18c-0.414,0-0.75-0.336-0.75-0.75v-3c0-0.414,0.336-0.75,0.75-0.75s0.75,0.336,0.75,0.75v2.25h1.902
          c0.414,0,0.75,0.336,0.75,0.75S20.317,18,19.903,18H17.251z"/>
          `;
        break;
      case 'alarm-clock':
        this.svgPaths = `
        <path d="M18,24c-0.286,0-0.543-0.159-0.671-0.415l-1.04-2.081C14.96,22.157,13.486,22.5,12,22.5c-1.486,0-2.96-0.343-4.289-0.995
          L6.67,23.585C6.543,23.841,6.286,24,6,24c-0.115,0-0.231-0.027-0.335-0.079c-0.37-0.185-0.521-0.636-0.336-1.006l1.089-2.177
          C3.802,18.906,2.25,15.945,2.25,12.75C2.25,7.374,6.624,3,12,3s9.75,4.374,9.75,9.75c0,3.195-1.552,6.156-4.168,7.987l1.089,2.177
          c0.185,0.37,0.034,0.821-0.335,1.006C18.231,23.973,18.115,24,18,24z M12,4.5c-4.549,0-8.25,3.701-8.25,8.25S7.451,21,12,21
          s8.25-3.701,8.25-8.25S16.549,4.5,12,4.5z"/>
        <path d="M8.609,13.5c-0.414,0-0.75-0.336-0.75-0.75S8.195,12,8.609,12h2.641V7.5c0-0.414,0.336-0.75,0.75-0.75
          s0.75,0.336,0.75,0.75v5.25c0,0.414-0.336,0.75-0.75,0.75H8.609z"/>
        <path d="M1.5,4.5c-0.229,0-0.443-0.103-0.586-0.281C0.656,3.896,0.708,3.423,1.031,3.164l3.75-3C4.914,0.058,5.08,0,5.25,0
          c0.229,0,0.443,0.103,0.586,0.282c0.258,0.323,0.206,0.796-0.117,1.054l-3.75,3C1.836,4.442,1.67,4.5,1.5,4.5z"/>
        <path d="M22.5,4.5c-0.169,0-0.335-0.058-0.468-0.164l-3.75-3c-0.323-0.258-0.375-0.731-0.117-1.054C18.307,0.103,18.521,0,18.75,0
          c0.169,0,0.335,0.058,0.468,0.164l3.751,3c0.323,0.258,0.375,0.731,0.117,1.054C22.942,4.397,22.729,4.5,22.5,4.5z"/>
          `;
        break;
      case 'alarm-bell':
        this.svgPaths = `
        <path d="M20.25,5.251c-0.287,0-0.552-0.167-0.676-0.425c-0.124-0.258-0.089-0.569,0.091-0.793l2.025-2.531H20.25
          c-0.414,0-0.75-0.336-0.75-0.75s0.336-0.75,0.75-0.75h3c0.287,0,0.552,0.167,0.676,0.425c0.124,0.258,0.089,0.569-0.091,0.793
          l-2.025,2.531h1.439c0.414,0,0.75,0.336,0.75,0.75s-0.336,0.75-0.75,0.75H20.25z"/>
        <path d="M14.25,12.001c-0.29,0-0.556-0.169-0.679-0.432c-0.123-0.262-0.083-0.576,0.103-0.799l2.725-3.27H14.25
          c-0.414,0-0.75-0.336-0.75-0.75s0.336-0.75,0.75-0.75H18c0.29,0,0.556,0.169,0.679,0.432c0.123,0.262,0.083,0.576-0.103,0.799
          l-2.725,3.27H18c0.414,0,0.75,0.336,0.75,0.75s-0.336,0.75-0.75,0.75H14.25z"/>
        <path d="M9.747,24.001c-0.47,0-0.939-0.12-1.356-0.348c-0.665-0.363-1.149-0.964-1.362-1.691c-0.116-0.397,0.112-0.815,0.509-0.931
          c0.069-0.02,0.14-0.03,0.211-0.03c0.33,0,0.626,0.221,0.719,0.538c0.101,0.343,0.329,0.626,0.642,0.797
          c0.197,0.107,0.417,0.164,0.638,0.164c0.127,0,0.255-0.019,0.38-0.055c0.438-0.129,0.777-0.468,0.906-0.906
          c0.093-0.317,0.389-0.539,0.72-0.539c0.071,0,0.142,0.01,0.21,0.03c0.398,0.117,0.626,0.534,0.51,0.931
          c-0.274,0.931-0.992,1.65-1.923,1.923C10.287,23.962,10.017,24.001,9.747,24.001z"/>
        <path d="M0.75,19.501c-0.274,0-0.526-0.149-0.657-0.389c-0.132-0.24-0.122-0.532,0.024-0.763C0.128,18.331,1.5,16.003,1.5,10.501
          c0-4.293,3.266-7.835,7.5-8.216V0.751c0-0.414,0.336-0.75,0.75-0.75s0.75,0.336,0.75,0.75v1.54
          c0.969,0.095,1.928,0.364,2.794,0.786c0.371,0.181,0.526,0.631,0.346,1.003c-0.127,0.26-0.385,0.422-0.675,0.422
          c-0.113,0-0.226-0.026-0.328-0.076c-0.896-0.436-1.895-0.67-2.89-0.674C6.027,3.751,3,6.779,3,10.501
          c0,3.812-0.609,6.207-1.087,7.5h15.623c-0.386-0.895-0.647-1.839-0.779-2.812c-0.027-0.198,0.025-0.396,0.146-0.555
          c0.121-0.159,0.297-0.262,0.496-0.289c0.033-0.005,0.068-0.007,0.103-0.007c0.372,0,0.691,0.279,0.741,0.65
          c0.162,1.2,0.552,2.34,1.156,3.388c0.134,0.231,0.134,0.519,0,0.75c-0.133,0.231-0.382,0.375-0.649,0.375H0.75z"/>
          `;
        break;
      case 'phone':
        this.svgPaths = `
        <path d="M18.023,24.039c-1.193,0-2.362-0.343-3.382-0.992c-0.003-0.002-0.026-0.017-0.029-0.018
          C9.219,19.383,4.637,14.8,0.993,9.406C-0.613,6.882-0.266,3.659,1.833,1.56l0.774-0.774c0.49-0.489,1.141-0.758,1.835-0.758
          c0.694,0,1.345,0.269,1.834,0.758c0,0,3.262,3.266,3.262,3.266c1.01,1.012,1.009,2.657-0.002,3.667
          C9.11,8.146,9.11,8.841,9.537,9.268c0,0,5.228,5.229,5.232,5.233c0.193,0.192,0.468,0.306,0.76,0.306
          c0.292,0,0.567-0.114,0.773-0.32c0.49-0.489,1.141-0.759,1.835-0.759s1.345,0.269,1.835,0.758l3.261,3.26
          c1.011,1.012,1.011,2.658,0,3.669l-0.774,0.774C21.266,23.382,19.69,24.039,18.023,24.039z M15.442,21.779
          c0.798,0.5,1.683,0.757,2.583,0.757c1.267,0,2.464-0.5,3.372-1.408l0.774-0.774c0.426-0.427,0.426-1.122,0-1.549l-3.26-3.259
          c-0.206-0.206-0.481-0.319-0.774-0.319s-0.568,0.114-0.774,0.32c-0.489,0.489-1.141,0.759-1.834,0.759
          c-0.689,0-1.337-0.266-1.824-0.75l-5.228-5.229c-1.011-1.012-1.011-2.658,0-3.669C8.901,6.233,8.903,5.54,8.478,5.113
          C8.473,5.108,8.456,5.09,8.451,5.085L5.215,1.846C5.009,1.641,4.734,1.528,4.442,1.528c-0.293,0-0.568,0.114-0.775,0.32
          L2.893,2.621C1.296,4.218,1.027,6.666,2.24,8.572C5.77,13.797,10.213,18.243,15.442,21.779L15.442,21.779z"/>
          `;
        break;
      case 'envelope':
        this.svgPaths = `
        <path d="M2.25,20.25C1.009,20.25,0,19.241,0,18V6c0-0.537,0.195-1.059,0.548-1.468c0.013-0.016,0.031-0.037,0.051-0.056
          C1.025,4.015,1.626,3.75,2.25,3.75h19.5c0.625,0,1.226,0.265,1.652,0.727c0.018,0.018,0.035,0.037,0.051,0.057
          C23.804,4.938,24,5.461,24,6v12c0,1.241-1.009,2.25-2.25,2.25H2.25z M1.5,18c0,0.414,0.336,0.75,0.75,0.75h19.5
          c0.414,0,0.75-0.336,0.75-0.75V6.187l-5.616,3.873l3.125,2.889c0.304,0.281,0.322,0.756,0.042,1.06
          c-0.142,0.153-0.343,0.241-0.551,0.241c-0.189,0-0.37-0.071-0.509-0.199l-3.373-3.118l-1.825,1.259
          c-0.526,0.364-1.146,0.556-1.793,0.556c-0.646,0-1.266-0.192-1.793-0.556l-1.825-1.258l-3.373,3.118
          C4.87,14.179,4.689,14.25,4.5,14.25c-0.208,0-0.409-0.088-0.551-0.24c-0.281-0.304-0.262-0.78,0.041-1.06l3.125-2.889L1.5,6.187V18
          z M11.059,10.957c0.276,0.191,0.601,0.291,0.941,0.291c0.34,0,0.665-0.101,0.941-0.291l8.276-5.707H2.783L11.059,10.957z"/>
          `;
        break;
      case 'settings':
        this.svgPaths = `
        <path d="M8.156,23.988c-0.676,0-1.323-0.25-1.822-0.704c-0.598-0.544-0.92-1.32-0.883-2.128l0.072-1.404
          c0.034-0.665-0.479-1.234-1.144-1.269c-0.021-0.001-0.042-0.002-0.062-0.002c-0.021,0-0.041,0-0.062,0.002l-1.401,0.071
          c-0.047,0.002-0.096,0.004-0.144,0.004c-0.668,0-1.311-0.247-1.809-0.696c-0.537-0.484-0.854-1.148-0.892-1.871
          c-0.043-0.817,0.281-1.6,0.887-2.149l1.042-0.94c0.239-0.216,0.38-0.513,0.397-0.836c0.016-0.323-0.094-0.633-0.31-0.873
          c-0.027-0.03-0.056-0.059-0.087-0.087L0.896,10.16C0.36,9.674,0.046,9.009,0.011,8.286C-0.025,7.564,0.224,6.871,0.71,6.335
          C1.222,5.77,1.952,5.446,2.712,5.446c0.044,0,0.089,0.001,0.133,0.003l1.402,0.071C4.268,5.522,4.31,5.522,4.31,5.522
          c0.644,0,1.174-0.503,1.208-1.144c0.002-0.042,0.002-0.085,0-0.127L5.451,2.845C5.381,1.352,6.538,0.081,8.03,0.011
          c0.043-0.002,0.086-0.003,0.129-0.003c0.76,0,1.489,0.323,2.001,0.886l0.941,1.039c0.217,0.241,0.513,0.382,0.835,0.399
          c0.022,0.001,0.044,0.002,0.066,0.002c0.301,0,0.579-0.107,0.805-0.31c0.031-0.028,0.06-0.057,0.087-0.088l0.95-1.042
          c0.483-0.535,1.148-0.85,1.871-0.887c0.046-0.002,0.091-0.003,0.136-0.003c0.673,0,1.319,0.248,1.818,0.699
          c0.607,0.548,0.931,1.331,0.889,2.148l-0.071,1.4c-0.033,0.665,0.481,1.233,1.146,1.267c0.021,0.001,0.041,0.002,0.061,0.002
          c0.019,0,0.038,0,0.057-0.001l1.4-0.071c0.047-0.002,0.092-0.003,0.138-0.003c0.672,0,1.316,0.249,1.815,0.702
          c0.536,0.486,0.851,1.152,0.887,1.874c0.04,0.815-0.284,1.596-0.889,2.143l-1.042,0.94c-0.239,0.216-0.38,0.513-0.396,0.836
          c-0.017,0.323,0.094,0.633,0.31,0.872c0.028,0.031,0.056,0.06,0.086,0.086l1.042,0.941c0.537,0.485,0.853,1.15,0.89,1.873
          c0.037,0.723-0.21,1.417-0.695,1.953c-0.513,0.567-1.247,0.892-2.015,0.892c0,0,0,0,0,0c-0.043,0-0.087-0.001-0.131-0.003
          l-1.402-0.071c-0.022-0.001-0.044-0.002-0.066-0.002c-0.302,0-0.58,0.108-0.806,0.311c-0.24,0.216-0.381,0.512-0.397,0.835
          c-0.002,0.041-0.002,0.083,0,0.125l0.071,1.4c0.032,0.725-0.219,1.418-0.708,1.953c-0.489,0.535-1.156,0.847-1.878,0.879
          c-0.043,0.002-0.088,0.003-0.133,0.003c-0.75,0-1.474-0.321-1.986-0.88l-0.944-1.044c-0.229-0.253-0.556-0.398-0.899-0.398
          c-0.3,0-0.587,0.11-0.809,0.311c-0.03,0.027-0.06,0.056-0.087,0.087l-0.946,1.042C9.649,23.666,8.918,23.988,8.156,23.988z
          M4.317,16.982c0.046,0,0.092,0.001,0.138,0.004c1.492,0.077,2.643,1.353,2.566,2.844l-0.072,1.4
          c-0.016,0.357,0.127,0.703,0.394,0.946c0.226,0.206,0.507,0.314,0.812,0.314c0.02,0,0.039,0,0.059-0.001
          c0.322-0.015,0.62-0.155,0.837-0.394l0.944-1.04c0.06-0.067,0.125-0.132,0.193-0.194c0.499-0.45,1.143-0.698,1.815-0.698
          c0.766,0,1.499,0.325,2.012,0.892l0.941,1.041c0.226,0.247,0.552,0.389,0.896,0.389l0.049-0.001
          c0.664-0.03,1.182-0.596,1.152-1.262l-0.071-1.395c-0.005-0.092-0.005-0.185,0-0.278c0.037-0.722,0.353-1.387,0.89-1.871
          c0.499-0.45,1.141-0.698,1.809-0.698c0.048,0,0.096,0.001,0.145,0.004l1.399,0.071c0.024,0.001,0.047,0.002,0.069,0.002
          c0.336,0,0.66-0.145,0.889-0.399c0.217-0.24,0.327-0.55,0.311-0.872c-0.016-0.323-0.157-0.619-0.397-0.836l-1.042-0.94
          c-0.068-0.061-0.133-0.126-0.195-0.195c-1.001-1.108-0.914-2.825,0.195-3.827l1.042-0.94c0.27-0.244,0.414-0.592,0.396-0.956
          c-0.016-0.323-0.156-0.62-0.396-0.837c-0.223-0.202-0.51-0.313-0.809-0.313c-0.021,0-0.041,0.001-0.062,0.002l-1.399,0.071
          c-0.044,0.002-0.089,0.003-0.133,0.003c-0.046,0-0.092-0.001-0.137-0.004c-1.491-0.075-2.643-1.349-2.568-2.84l0.071-1.401
          c0.019-0.365-0.126-0.714-0.397-0.958c-0.226-0.205-0.503-0.312-0.802-0.312c-0.022,0-0.045,0.001-0.068,0.002
          c-0.324,0.016-0.621,0.157-0.837,0.397l-0.948,1.04c-0.059,0.066-0.126,0.133-0.196,0.196c-0.498,0.449-1.142,0.696-1.814,0.696
          c-0.046,0-0.092-0.001-0.138-0.003c-0.723-0.038-1.387-0.354-1.871-0.891L9.05,1.902c-0.228-0.25-0.554-0.394-0.894-0.394
          c-0.019,0-0.038,0-0.057,0.001C7.434,1.54,6.918,2.108,6.949,2.774l0.067,1.4c0.005,0.091,0.005,0.187,0,0.283
          C6.94,5.895,5.752,7.022,4.311,7.022c-0.047,0-0.094-0.001-0.141-0.004L2.771,6.948C2.753,6.947,2.733,6.946,2.714,6.946
          c-0.339,0-0.665,0.145-0.893,0.396c-0.217,0.239-0.328,0.548-0.312,0.87c0.016,0.322,0.156,0.619,0.395,0.836l1.041,0.945
          c0.067,0.061,0.132,0.125,0.193,0.194c1.001,1.109,0.914,2.826-0.195,3.827l-1.041,0.94c-0.27,0.244-0.414,0.594-0.395,0.958
          c0.017,0.322,0.158,0.619,0.398,0.835c0.222,0.2,0.509,0.31,0.807,0.31c0.021,0,0.043,0,0.064-0.002l1.401-0.071
          C4.224,16.983,4.27,16.982,4.317,16.982z"/>
        <path d="M12,17.25c-2.895,0-5.25-2.355-5.25-5.25S9.105,6.75,12,6.75s5.25,2.355,5.25,5.25S14.895,17.25,12,17.25z M12,8.25
          c-2.068,0-3.75,1.682-3.75,3.75s1.682,3.75,3.75,3.75s3.75-1.682,3.75-3.75S14.068,8.25,12,8.25z"/>
          `;
        break;
      case 'no-image':
        this.svgPaths = `
          <g>
            <circle cx="17.249" cy="20.625" r="1.125"/>
            <path d="M17.249,18.75c-0.414,0-0.75-0.336-0.75-0.75v-3c0-0.414,0.336-0.75,0.75-0.75s0.75,0.336,0.75,0.75v3
              C17.999,18.414,17.663,18.75,17.249,18.75z"/>
            <path d="M13.023,24c-1.391,0-2.524-1.132-2.525-2.523c0-0.39,0.092-0.781,0.267-1.131l4.226-8.451
              c0.43-0.861,1.297-1.396,2.262-1.396c0.393,0,0.771,0.089,1.124,0.266c0.486,0.243,0.887,0.644,1.13,1.13l4.226,8.451
              c0.623,1.244,0.117,2.764-1.127,3.386C22.256,23.908,21.865,24,21.475,24H13.023z M17.252,12c-0.392,0-0.744,0.217-0.918,0.567
              l-4.226,8.451c-0.071,0.142-0.109,0.3-0.109,0.459c0,0.565,0.46,1.024,1.024,1.024h8.452c0.158,0,0.317-0.038,0.46-0.109
              c0.504-0.252,0.709-0.869,0.457-1.374l-4.226-8.451c-0.099-0.197-0.261-0.36-0.458-0.459C17.564,12.036,17.41,12,17.252,12z"/>
            <path d="M2.249,21c-1.241,0-2.25-1.009-2.25-2.25V2.25c0-1.241,1.009-2.25,2.25-2.25h10.629c0.601,0,1.165,0.234,1.59,0.658
              l2.872,2.872c0.425,0.425,0.659,0.99,0.659,1.59V8.25c0,0.414-0.336,0.75-0.75,0.75s-0.75-0.336-0.75-0.75V5.121
              c0-0.197-0.08-0.39-0.219-0.53l-2.872-2.872c-0.142-0.141-0.33-0.219-0.53-0.219H2.249c-0.414,0-0.75,0.336-0.75,0.75v16.5
              c0,0.414,0.336,0.75,0.75,0.75h5.25c0.414,0,0.75,0.336,0.75,0.75S7.913,21,7.499,21H2.249z"/>
            <circle cx="5.249" cy="6" r="1.125"/>
            <path d="M3.749,15c-0.26,0-0.498-0.132-0.636-0.352c-0.138-0.221-0.153-0.492-0.038-0.726l1.937-3.981
              c0.015-0.03,0.031-0.058,0.049-0.085c0.025-0.038,0.051-0.073,0.078-0.107C5.426,9.39,5.853,9.186,6.311,9.186
              c0.339,0,0.672,0.117,0.937,0.329l0.608,0.487l1.541-2.197C9.505,7.651,9.639,7.52,9.796,7.416c0.246-0.163,0.532-0.25,0.828-0.25
              c0.504,0,0.972,0.25,1.25,0.669l1.618,2.588c0.219,0.351,0.112,0.814-0.239,1.033c-0.12,0.075-0.257,0.114-0.397,0.114
              c-0.26,0-0.498-0.132-0.636-0.352l-1.606-2.57l-1.988,2.87c-0.118,0.169-0.304,0.283-0.508,0.312
              c-0.036,0.005-0.071,0.007-0.106,0.007c-0.17,0-0.337-0.059-0.469-0.165L6.316,10.69L4.948,13.5h4.801
              c0.414,0,0.75,0.336,0.75,0.75S10.163,15,9.749,15H3.749z"/>
          </g>
          `;
        break;
      case 'sorting-up':
        this.svgPaths = `
        <path d="M20,3.4h-9V1.9h9V3.4z M20,5.9h-7v1.5h7V5.9z M20,9.9h-9v1.5h9V9.9z M20,13.9H10v1.5h10V13.9z
        M20,17.9H9v1.5h11V17.9z M20,21.9H8v1.5h12V21.9z M10.2,5.1L5.8,0.6c-0.3-0.3-0.8-0.3-1,0l0,0c0,0,0,0,0,0c-0.1,0-0.2,0.1-0.3,0.2
       L0.3,5.1C0,5.4,0,5.9,0.3,6.1l0,0c0.3,0.3,0.8,0.3,1,0l3.1-3.2v21c0,0.4,0.3,0.7,0.7,0.7h0c0.4,0,0.7-0.3,0.7-0.7v-21l3.2,3.2
       c0.3,0.3,0.8,0.3,1,0l0,0C10.5,5.8,10.5,5.4,10.2,5.1z"/>
          `;
        break;
      case 'sorting-down':
        this.svgPaths = `
        <path d="M8,1.4h12v1.5H8V1.4z M9,5.4h11v1.5H9V5.4z M10,9.4h10v1.5H10V9.4z M11,13.4h9v1.5h-9V13.4z M13,17.4
        h7v1.5h-7V17.4z M11,21.4h9v1.5h-9V21.4z M0.2,19.6L4.6,24c0.3,0.3,0.8,0.3,1,0l0,0c0,0,0,0,0,0c0.1,0,0.2-0.1,0.3-0.2l4.1-4.2
        c0.3-0.3,0.3-0.8,0-1l0,0c-0.3-0.3-0.8-0.3-1,0l-3.1,3.2v-21C5.9,0.3,5.6,0,5.2,0h0C4.8,0,4.4,0.3,4.4,0.7v21l-3.2-3.2
        c-0.3-0.3-0.8-0.3-1,0l0,0C-0.1,18.8-0.1,19.3,0.2,19.6z"/>
        `;
        break;
    }

    // Set the path of the SVG
    if (this.innerSVG !== undefined) {
      this.innerSVG.nativeElement.innerHTML = this.svgPaths;
    } else {
      // console.log('Cannot read SVG path');
    }
  }
  get type(): IconTypes {
    return this._type;
  }

  // ---------------------------------------- Methods ----------------------------------------
  constructor(private cd: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    //Called after ngOnInit when the component's or directive's content has been initialized.
    //Add 'implements AfterContentInit' to the class.
    this.setProperties();
  }

  setProperties() {
    if (this.isActive) {
      this.icon.nativeElement.className = this.size + this.active + this.hover + this.hasBackground;
    } else if (!this.isActive) {
      if (this.isClickable) {
        this.icon.nativeElement.className = this.size + this.color + this.hover + this.hasBackground;
      } else {
        this.icon.nativeElement.className = this.size + this.color + this.hasBackground;
      }
    }
    this.cd.detectChanges();
  }

  @Input() set hasBackground(value: string) {
    switch (value) {
      case 'true':
        this._hasBackground = ' has-bg';
        break;
      case 'false':
        this._hasBackground = ' no-bg';
        break;
      default:
        this._hasBackground = ' no-bg';
        break;
    }
  }

  get hasBackground(): string {
    return returnOrDefault(this._hasBackground, ' no-bg');
  }

  ngOnInit() {}

  ngOnDestroy(): void {}
}
